import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Gantt } from "wx-react-gantt";
import { useEemData } from "../context/EemDataContext";
import { fetchProjectMeasures, fetchGanttData, getProjectIdFromQuery, saveGanttStructure, reCalculatePhasedEem } from "../services/heliosApi";
import { transformEemToTasks } from "../utils/transformEemData";
import { buildDeerMeta } from "../utils/deerMappings";
import { transformToBackendFormat, parseDateLocal } from "../utils/transformGanttData";
import {
  MdAdd,
  MdDelete,
  MdKeyboardArrowUp,
  MdKeyboardArrowDown,
  MdEdit,
  MdAutoAwesome,
  MdRefresh,
  MdRestartAlt,
  MdSearch,
  MdClose,
  MdTune,
  MdSwapVert,
} from "react-icons/md";
import { VscSave } from "react-icons/vsc";
import { FaIndent } from "react-icons/fa";
import { toast } from 'react-toastify';
import "./SvarGantt.css";

// Custom Toolbar Component
const CustomToolbar = ({
  apiRef,
  recalculating,
  onRefresh,
  onAutoCreatePhases,
  onGroupByCategory,
  onGroupBySubCategory,
  onSave,
  onSaveAndRecalculate,
  onAddPhase,
  onRearrangePhases,
  searchTerm,
  onSearchTermChange,
  onClearSearch,
  searchStats,
  searchInputRef,
  showFilters,
  onToggleFilters,
  categoryOptions,
  subCategoryOptions,
  selectedCategory,
  selectedSubCategory,
  onCategoryChange,
  onSubCategoryChange,
}) => {
  const phaseMenuRef = useRef(null);
  const [isPhaseMenuOpen, setIsPhaseMenuOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    phaseId: null,
    phaseName: "",
    measures: [],
  });

  useEffect(() => {
    if (!deleteModal.open) return undefined;

    const handleEsc = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        setDeleteModal({ open: false, phaseId: null, phaseName: "", measures: [] });
      }
    };

    document.addEventListener("keydown", handleEsc, true);
    return () => {
      document.removeEventListener("keydown", handleEsc, true);
    };
  }, [deleteModal.open]);

  useEffect(() => {
    if (!isPhaseMenuOpen) return undefined;

    const handleOutside = (e) => {
      if (!phaseMenuRef.current) return;
      if (!phaseMenuRef.current.contains(e.target)) {
        setIsPhaseMenuOpen(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        setIsPhaseMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside, true);
    document.addEventListener("keydown", handleEsc, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside, true);
      document.removeEventListener("keydown", handleEsc, true);
    };
  }, [isPhaseMenuOpen]);

  const handleAddTask = () => {
    if (onAddPhase) {
      onAddPhase();
    }
  };

  const handleDeleteTask = () => {
    const api = apiRef.current;
    if (api) {
      const state = api.getState();
      const selected = state.selected;
      const taskId = Array.isArray(selected) ? selected[0] : selected;
      if (taskId) {
        const selectedTask = state._tasks?.find(t => t.id === taskId);
        if (selectedTask && !selectedTask.isPhase) {
          toast.info('You cannot delete a measure', { autoClose: 2000 });
          return;
        }
        if (selectedTask?.isPhase) {
          const phaseName = String(selectedTask.text || "Phase").trim();
          const allTasks = state._tasks || [];
          const measures = allTasks
            .filter(t => t && !t.isPhase && t.parent === selectedTask.id)
            .map(t => String(t.text || "Unnamed measure"));
          setDeleteModal({
            open: true,
            phaseId: selectedTask.id,
            phaseName,
            measures,
          });
          return;
        }
        api.exec('delete-task', { id: taskId });
      }
    }
  };

  const handleConfirmDeletePhase = () => {
    const api = apiRef.current;
    const phaseId = deleteModal.phaseId;
    setDeleteModal({ open: false, phaseId: null, phaseName: "", measures: [] });
    if (api && phaseId) {
      api.exec("delete-task", { id: phaseId });
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({ open: false, phaseId: null, phaseName: "", measures: [] });
  };

  const handleIndent = () => {
    const api = apiRef.current;
    if (api) {
      const state = api.getState();
      const selected = state.selected;
      const taskId = Array.isArray(selected) ? selected[0] : selected;
      if (taskId) {
        const selectedTask = state._tasks?.find(t => t.id === taskId);
        if (selectedTask?.isPhase) {
          toast.info('A phase cannot be nested inside another phase', { autoClose: 2000 });
          return;
        }
        api.exec('indent-task', { id: taskId, mode: 'add' });
      }
    }
  };

  const handleMoveUp = () => {
    const api = apiRef.current;
    if (api) {
      const selected = api.getState().selected;
      const taskId = Array.isArray(selected) ? selected[0] : selected;
      if (taskId) {
        api.exec('move-task', { id: taskId, mode: 'up' });
      }
    }
  };

  const handleMoveDown = () => {
    const api = apiRef.current;
    if (api) {
      const selected = api.getState().selected;
      const taskId = Array.isArray(selected) ? selected[0] : selected;
      if (taskId) {
        api.exec('move-task', { id: taskId, mode: 'down' });
      }
    }
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleRearrangePhases = () => {
    if (onRearrangePhases) {
      onRearrangePhases();
    }
  };

  const handleEdit = () => {
    const api = apiRef.current;
    if (api) {
      const selected = api.getState().selected;
      const taskId = Array.isArray(selected) ? selected[0] : selected;
      if (taskId) {
        const state = api.getState();
        const currentTask = state._tasks?.find(t => t.id === taskId);
        if (currentTask && currentTask.isPhase) {
          // Open editor for the selected phase only
          api.exec('show-editor', { id: taskId });
        } else {
          toast.info('Only phases can be edited', { autoClose: 2000 });
        }
      }
    }
  };

  const handleAutoCreateClick = () => {
    setIsPhaseMenuOpen(false);
    if (onAutoCreatePhases) onAutoCreatePhases();
  };

  const handleGroupByCategoryClick = () => {
    setIsPhaseMenuOpen(false);
    if (onGroupByCategory) onGroupByCategory();
  };

  const handleGroupBySubCategoryClick = () => {
    setIsPhaseMenuOpen(false);
    if (onGroupBySubCategory) onGroupBySubCategory();
  };

  const handleGroupByEngineeringPriorityClick = () => {
    setIsPhaseMenuOpen(false);
  };

  const handleGroupByLaborIntensityClick = () => {
    setIsPhaseMenuOpen(false);
  };

  const handleTogglePhaseMenu = () => {
    setIsPhaseMenuOpen((prev) => !prev);
  };

  return (
    <div className="gantt-toolbar">
      <div className="toolbar-main-row">
        <div className="toolbar-left">
          <button className="icon-btn" onClick={handleAddTask} title="Add phase">
            <span className="icon"><MdAdd size={16} /></span>
            <span>Add</span>
          </button>
          <button className="icon-btn" onClick={handleDeleteTask} title="Delete phase">
            <span className="icon"><MdDelete size={16} /></span>
            <span>Delete</span>
          </button>
          <button className="icon-btn" onClick={handleEdit} title="Edit phase">
            <span className="icon"><MdEdit size={16} /></span>
            <span>Edit</span>
          </button>
          <div className="divider"></div>
          <button className="icon-btn" onClick={handleIndent} title="Indent">
            <span className="icon"><FaIndent size={14} /></span>
            <span>Indent</span>
          </button>
          <button className="icon-btn" onClick={handleMoveUp} title="Move up">
            <span className="icon"><MdKeyboardArrowUp size={18} /></span>
            <span>Up</span>
          </button>
          <button className="icon-btn" onClick={handleMoveDown} title="Move down">
            <span className="icon"><MdKeyboardArrowDown size={18} /></span>
            <span>Down</span>
          </button>
          <button className="icon-btn" onClick={handleRearrangePhases} title="Rearrange phase order">
            <span className="icon"><MdSwapVert size={16} /></span>
            <span>Reorder</span>
          </button>
          <div className="divider"></div>
          <button
            className={`icon-btn ${showFilters ? "is-active" : ""}`}
            onClick={onToggleFilters}
            title="Toggle filters"
          >
            <span className="icon"><MdTune size={16} /></span>
            <span>Filters</span>
          </button>
          <button className="icon-btn refresh-labeled" onClick={handleRefresh} title="Reset data">
            <span className="icon"><MdRestartAlt size={16} /></span>
            <span>Refresh</span>
          </button>
        </div>

        <div className="toolbar-right">
          <div className="toolbar-right-actions">
            <div className="split-btn-wrap" ref={phaseMenuRef}>
              <button
                className="split-btn-main"
                onClick={handleTogglePhaseMenu}
                title="Create phase options"
              >
                <MdAutoAwesome size={15} />
                <span>Create Phase</span>
              </button>
              <button
                className={`split-btn-arrow ${isPhaseMenuOpen ? "is-open" : ""}`}
                onClick={handleTogglePhaseMenu}
                title="Phase options"
                aria-expanded={isPhaseMenuOpen}
                aria-haspopup="menu"
              >
                <MdKeyboardArrowDown size={16} />
              </button>
              <div className={`split-dropdown ${isPhaseMenuOpen ? "open" : ""}`} role="menu">
                <div className="dropdown-header">Create Phase</div>
                <button className="dropdown-item" onClick={handleAutoCreateClick} role="menuitem">
                  <span className="dropdown-item-icon blue">✦</span>
                  <span className="dropdown-item-content">
                    <span className="dropdown-item-title">Auto-create Phases</span>
                    <span className="dropdown-item-desc">Automatically generate phases from measures</span>
                  </span>
                </button>
                <button className="dropdown-item" onClick={handleGroupByCategoryClick} role="menuitem">
                  <span className="dropdown-item-icon teal">⊞</span>
                  <span className="dropdown-item-content">
                    <span className="dropdown-item-title">Group by Category</span>
                    <span className="dropdown-item-desc">Organize phases by measure category</span>
                  </span>
                </button>
                <button className="dropdown-item" onClick={handleGroupBySubCategoryClick} role="menuitem">
                  <span className="dropdown-item-icon purple">⊟</span>
                  <span className="dropdown-item-content">
                    <span className="dropdown-item-title">Group by Subcategory</span>
                    <span className="dropdown-item-desc">Organize phases by measure subcategory</span>
                  </span>
                </button>
                <button className="dropdown-item" onClick={handleGroupByEngineeringPriorityClick} role="menuitem">
                  <span className="dropdown-item-icon blue">⚙</span>
                  <span className="dropdown-item-content">
                    <span className="dropdown-item-title">Group by Engineering Priority</span>
                    <span className="dropdown-item-desc">Coming soon</span>
                  </span>
                </button>
                <button className="dropdown-item" onClick={handleGroupByLaborIntensityClick} role="menuitem">
                  <span className="dropdown-item-icon teal">⌁</span>
                  <span className="dropdown-item-content">
                    <span className="dropdown-item-title">Group by Labor Intensity</span>
                    <span className="dropdown-item-desc">Coming soon</span>
                  </span>
                </button>
              </div>
            </div>
            <button
              className="btn btn-primary"
              onClick={onSave}
              title="Save Structure"
            >
              <VscSave size={16} />
              <span>Save</span>
            </button>
            <button
              className="btn btn-accent"
              onClick={onSaveAndRecalculate}
              disabled={recalculating}
              title="Save and Recalculate Phased EEM"
            >
              {recalculating ? (
                <>
                  <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>
                    <MdRefresh size={16} />
                  </span>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <MdRefresh size={16} />
                  <span>Save & Recalculate</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {showFilters && (
        <div className="search-bar-wrap">
          <div className={`search-input-wrap ${searchTerm ? "has-value" : ""} ${searchStats?.isNoMatch ? "is-no-match" : ""}`}>
            <span className="search-icon">
              <MdSearch size={14} />
            </span>
            <input
              ref={searchInputRef}
              className="search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              placeholder="Search measures..."
            />
            {searchTerm && (
              <button className="search-clear" onClick={onClearSearch} title="Clear search">
                <MdClose size={14} />
              </button>
            )}
          </div>
          <select
            className="filter-select"
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            title="Filter by category"
          >
            <option value="">All categories</option>
            {categoryOptions.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={selectedSubCategory}
            onChange={(e) => onSubCategoryChange(e.target.value)}
            title="Filter by subcategory"
          >
            <option value="">All subcategories</option>
            {subCategoryOptions.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
          {searchTerm && (
            <div className="search-results-count">
              <div className={searchStats?.isNoMatch ? "search-result-pill no-match" : "search-result-pill"}>
                {searchStats?.isNoMatch
                  ? "No matches found"
                  : `${searchStats?.count || 0} ${searchStats?.label || "matches"} found`}
              </div>
            </div>
          )}
        </div>
      )}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      {deleteModal.open && createPortal(
        <div className="helios-delete-modal-overlay" onClick={handleCloseDeleteModal}>
          <div
            className="helios-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Delete phase confirmation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="helios-delete-modal-top">
              <div className="helios-delete-modal-icon">
                <MdDelete size={20} />
              </div>
              <div className="helios-delete-modal-text">
                <div className="helios-delete-modal-title">
                  Delete <span className="phase-name">"{deleteModal.phaseName}"</span>?
                </div>
                <div className="helios-delete-modal-desc">
                  This will permanently remove the phase. This action cannot be undone.
                </div>
              </div>
            </div>

            <div className="helios-delete-modal-divider" />
            <div className="helios-delete-modal-actions">
              <button type="button" className="helios-delete-btn-cancel" onClick={handleCloseDeleteModal}>
                Cancel
              </button>
              <button type="button" className="helios-delete-btn-danger" onClick={handleConfirmDeletePhase}>
                <MdDelete size={16} />
                <span>Delete Phase</span>
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

const SvarBasicGantt = () => {
  const { eemTasks, loading, setEemTasks, setLoading } = useEemData();
  const MIN_YEAR = 2025;
  const MAX_YEAR = 2060;
  const ROW_BLOCK_HEIGHT = 44;
  const apiRef = useRef();
  const searchInputRef = useRef(null);
  const editorSaveStateRef = useRef({ suppressToast: false });
  const openEditorOnAddRef = useRef(false);
  const pendingNewPhaseIdRef = useRef(null);
  const clickScrollGuardRef = useRef({ active: false, startX: 0, startY: 0, snapshot: [] });
  const [apiReady, setApiReady] = useState(false);
  // Bumped every time the Gantt (re)mounts and hands us a fresh api instance.
  // Effects that register api.on/api.intercept handlers must depend on this,
  // or their registrations die whenever a rebuild remounts the gantt.
  const [apiInitVersion, setApiInitVersion] = useState(0);
  const [recalculating, setRecalculating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchStats, setSearchStats] = useState({ count: 0, label: "matches", isNoMatch: false });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [ganttRenderVersion, setGanttRenderVersion] = useState(0);

  const handleCategoryChange = useCallback((value) => {
    setSelectedCategory(value);
    if (!value) {
      // "All categories" should always restore full unfiltered view.
      setSelectedSubCategory("");
    }
  }, []);

  const handleSubCategoryChange = useCallback((value) => {
    setSelectedSubCategory(value);
  }, []);

  // Re-attach visual classes after library DOM re-renders (e.g., expand/collapse).
  const applyPhaseMeasureClasses = useCallback((options = {}) => {
    // skipLabels: label text sync rewrites the library's managed text nodes,
    // which is only safe in quiet moments (initial load, clicks, data
    // changes). High-frequency callers (the MutationObserver) must skip it or
    // the library's renderer can corrupt and lock up on the next move.
    const { skipLabels = false } = options;
    const api = apiRef.current;
    const flattenTasks = (input) => {
      if (!input) return [];
      if (Array.isArray(input)) {
        const out = [];
        const walk = (task) => {
          if (!task || typeof task !== "object") return;
          out.push(task);
          if (Array.isArray(task.data)) {
            task.data.forEach(walk);
          }
        };
        input.forEach(walk);
        return out;
      }
      if (typeof input === "object") {
        return Object.values(input).filter((task) => task && typeof task === "object");
      }
      return [];
    };

    const state = api?.getState?.();
    const rawTasks =
      state?.tasks?._tasks ||
      state?.tasks?.data ||
      state?.tasks ||
      state?._tasks ||
      [];

    const sourceTasks = flattenTasks(rawTasks).length ? flattenTasks(rawTasks) : eemTasks;
    if (!sourceTasks || sourceTasks.length === 0) return;

    const tasksById = new Map(sourceTasks.map((task) => [String(task.id), task]));
    const eemTasksById = new Map(
      (Array.isArray(eemTasks) ? eemTasks : []).map((task) => [String(task.id), task])
    );
    const resolveTaskById = (id) => {
      if (!id) return null;
      const fromApi = api?.getTask?.(id);
      if (fromApi) return fromApi;
      return tasksById.get(String(id)) || eemTasksById.get(String(id)) || null;
    };

    document.querySelectorAll('.wx-bar[data-id]').forEach((barEl) => {
      const id = barEl.getAttribute('data-id');
      if (!id) return;
      const task = resolveTaskById(id);
      if (!task) return;

      if (task?.isPhase) {
        barEl.classList.add('phase-bar');
        barEl.classList.remove('item-bar');
      } else {
        barEl.classList.add('item-bar');
        barEl.classList.remove('phase-bar');
      }
    });

    document.querySelectorAll('.wx-row[data-id], .wx-grid-row[data-id]').forEach((rowEl) => {
      const id = rowEl.getAttribute('data-id');
      if (!id) return;
      const task = resolveTaskById(id);
      if (!task) return;

      const nameCell =
        rowEl.querySelector('.wx-cell[data-col-id="text"]') ||
        rowEl.querySelector('.wx-cell[data-column="text"]') ||
        rowEl.querySelector('.wx-cell');
      if (nameCell) {
        nameCell.classList.add('name-cell');
      }

      if (task?.isPhase) {
        rowEl.classList.add('phase-row');
        rowEl.classList.remove('item-row');
        const collapseButton = rowEl.querySelector('.wx-collapse, .wx-toggle-icon');
        if (collapseButton) {
          collapseButton.classList.add('collapse-btn');
        }
      } else {
        rowEl.classList.add('item-row');
        rowEl.classList.remove('phase-row');
      }

      // Keep grid indentation in sync with the real tree depth — the library
      // can leave a stale inline padding on rows after a corrective move.
      const contentEl = rowEl.querySelector('.wx-content');
      if (contentEl) {
        let depth = 0;
        let cursor = task;
        let steps = 0;
        while (cursor && steps < 50) {
          const parentId = cursor.parent;
          if (parentId === 0 || parentId === "0" || parentId === null || parentId === undefined) break;
          depth += 1;
          cursor = resolveTaskById(parentId);
          steps += 1;
        }
        const desiredPad = `${depth * 20}px`;
        if (contentEl.style.paddingLeft !== desiredPad) {
          contentEl.style.paddingLeft = desiredPad;
        }
      }
    });

    // Keep phase bar labels in sync with renamed phase text (e.g., Phase 1, Phase 2, ...).
    if (!skipLabels) document.querySelectorAll('.wx-bar.phase-bar[data-id]').forEach((barEl) => {
      const id = barEl.getAttribute('data-id');
      if (!id) return;
      const task = resolveTaskById(id);
      if (!task || !task.isPhase) return;

      const desiredText = String(task.text || "").trim();
      if (!desiredText) return;

      const labelEl =
        barEl.querySelector('.wx-content') ||
        barEl.querySelector('.wx-label') ||
        barEl.querySelector('.wx-bar-content') ||
        barEl.querySelector('span');

      if (labelEl && labelEl.textContent !== desiredText) {
        labelEl.textContent = desiredText;
      }
    });

    if (!skipLabels) document.querySelectorAll('.wx-bar[data-id]:not(.phase-bar)').forEach((barEl) => {
      const id = barEl.getAttribute('data-id');
      if (!id) return;
      const liveTask = resolveTaskById(id) || tasksById.get(id);
      const eemTask = eemTasksById.get(String(id));
      const task = { ...(eemTask || {}), ...(liveTask || {}) };
      if (!task) return;

      const description = String(
        task?.barDescription ||
        task?.details ||
        task?.text ||
        eemTask?.barDescription ||
        eemTask?.details ||
        ""
      ).replace(/\s+/g, " ").trim();

      const category = String(
        task?.categoryLabel ||
        task?.originalMeasure?.doeCategory ||
        task?.originalMeasure?.ecmCategory ||
        task?.originalMeasure?.category ||
        task?.originalMeasure?.ecmCategoryId ||
        task?.doeCategory ||
        task?.ecmCategory ||
        task?.category ||
        task?.ecmCategoryId ||
        eemTask?.categoryLabel ||
        eemTask?.originalMeasure?.doeCategory ||
        eemTask?.originalMeasure?.ecmCategory ||
        eemTask?.originalMeasure?.category ||
        ""
      ).replace(/^Category:\s*/i, "").trim();

      const subCategory = String(
        task?.subCategoryLabel ||
        task?.originalMeasure?.doeSubCategory ||
        task?.originalMeasure?.ecmSubCategory ||
        task?.originalMeasure?.subCategory ||
        task?.originalMeasure?.ecmSubCategoryId ||
        task?.doeSubCategory ||
        task?.ecmSubCategory ||
        task?.subCategory ||
        task?.ecmSubCategoryId ||
        eemTask?.subCategoryLabel ||
        eemTask?.originalMeasure?.doeSubCategory ||
        eemTask?.originalMeasure?.ecmSubCategory ||
        eemTask?.originalMeasure?.subCategory ||
        ""
      ).replace(/^Subcategory:\s*/i, "").trim();

      const metaFromBarMeta = String(task?.barMeta || eemTask?.barMeta || "");
      const categoryFromMeta = (metaFromBarMeta.match(/Category:\s*([^•|]+)/i)?.[1] || "").trim();
      const subCategoryFromMeta = (metaFromBarMeta.match(/Subcategory:\s*([^•|]+)/i)?.[1] || "").trim();
      const finalCategory = category || categoryFromMeta;
      const finalSubCategory = subCategory || subCategoryFromMeta;
      const metaLine = [finalCategory, finalSubCategory].filter(Boolean).join("  |  ");
      const barLines = [description, metaLine].filter(Boolean).join("\n");
      if (!barLines) return;

      const labelEl =
        barEl.querySelector('.wx-content') ||
        barEl.querySelector('.wx-label') ||
        barEl.querySelector('.wx-bar-content') ||
        barEl.querySelector('span');
      if (labelEl && labelEl.textContent !== barLines) {
        labelEl.textContent = barLines;
      }
    });

    // Right pane has no row elements (bars are absolutely positioned in .wx-area),
    // so phase row shading must be drawn as overlay strips derived from bar positions.
    const syncPhaseOverlays = () => {
      const area = document.querySelector('.wx-chart .wx-area');
      if (!area) return;
      let host = area.querySelector(':scope > .phase-overlays-host');
      const phaseBars = [...area.querySelectorAll('.wx-bar.phase-bar')];
      if (!phaseBars.length) {
        if (host) host.remove();
        return;
      }
      if (!host) {
        host = document.createElement('div');
        host.className = 'phase-overlays-host';
        area.insertBefore(host, area.firstChild);
      }
      const signature = phaseBars.map((bar) => bar.style.top).join(',');
      if (host.dataset.signature === signature) return;
      host.dataset.signature = signature;
      host.innerHTML = '';
      phaseBars.forEach((bar) => {
        const barTop = parseFloat(bar.style.top) || 0;
        const rowTop = Math.round(barTop / ROW_BLOCK_HEIGHT) * ROW_BLOCK_HEIGHT;
        const strip = document.createElement('div');
        strip.className = 'phase-row-overlay';
        strip.style.top = `${rowTop}px`;
        strip.style.height = `${ROW_BLOCK_HEIGHT}px`;
        host.appendChild(strip);
      });
    };

    syncPhaseOverlays();

  }, [eemTasks]);

  // Rebuild the task list so that phase `phaseId` sits at root level directly
  // above (before=true) or below the root task `anchorId`, then remount the
  // gantt. Library move-task execs are avoided on purpose: their semantics
  // nest a task dropped "after" an open phase, and their internal processing
  // has hung the renderer on nested-phase structures.
  const rebuildWithPhaseAt = useCallback((phaseId, anchorId, before) => {
    const api = apiRef.current;
    const store = api?.getState?.()?.tasks;
    if (!store || typeof store.getBranch !== "function") return false;
    const pool = store._pool;
    const poolTasks = pool
      ? (pool instanceof Map ? [...pool.values()] : Object.values(pool))
      : [];
    const phaseTask = poolTasks.find(t => t && t.id === phaseId);
    if (!phaseTask) return false;

    const rootBranch = (store.getBranch(anchorId) || []).filter(t => t && t.id !== phaseId);
    const anchorIdx = rootBranch.findIndex(t => t.id === anchorId);
    if (anchorIdx < 0) return false;
    rootBranch.splice(anchorIdx + (before ? 0 : 1), 0, phaseTask);

    const eemById = new Map(
      (Array.isArray(eemTasks) ? eemTasks : []).map(t => [String(t.id), t])
    );
    const cleanTask = (t, parent) => {
      const merged = { ...(eemById.get(String(t.id)) || {}), ...t, parent };
      delete merged.data;
      Object.keys(merged).forEach(k => { if (k.startsWith("$")) delete merged[k]; });
      return merged;
    };
    const rebuilt = [];
    rootBranch.forEach(item => {
      rebuilt.push(cleanTask(item, 0));
      poolTasks
        .filter(k => k && k.id !== 0 && k.id !== item.id && !k.isPhase && k.parent === item.id)
        .forEach(k => rebuilt.push(cleanTask(k, item.id)));
    });

    setEemTasks(rebuilt);
    setGanttRenderVersion(prev => prev + 1);
    return true;
  }, [eemTasks, setEemTasks]);

  // Phases must always stay at root level. Whatever the source of a move
  // (row drag, indent, exec), if a phase ends up inside another task's
  // subtree, re-root it as a sibling of that subtree's root — above it when
  // it was dropped in the upper half of the subtree, below it otherwise.
  const lastPhaseFixRef = useRef({ id: null, at: 0 });
  const enforcePhaseRootLevel = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    // state._tasks is the original input array and never reflects moves;
    // the live tree lives in the tasks store (_pool + byId/getBranch).
    const store = api.getState?.()?.tasks;
    if (!store || typeof store.byId !== "function") return;
    const pool = store._pool;
    const poolTasks = pool
      ? (pool instanceof Map ? [...pool.values()] : Object.values(pool))
      : [];
    if (!poolTasks.length) return;

    const isRootParent = (p) => p === 0 || p === "0" || p === null || p === undefined;
    const nestedPhase = poolTasks.find(
      t => t && t.id !== 0 && t.isPhase && !isRootParent(t.parent)
    );
    if (!nestedPhase) {
      // Everything is clean — allow future corrections for any phase again.
      lastPhaseFixRef.current = { id: null, at: 0, attempts: 0 };
      return;
    }

    // Safety valve + circuit breaker: never re-correct the same phase in a
    // tight loop, and give up (loudly) instead of retrying forever if the
    // correction doesn't stick.
    const now = Date.now();
    const rec = lastPhaseFixRef.current;
    if (rec.id === nestedPhase.id) {
      if (now - rec.at < 400) return;
      rec.attempts = (rec.attempts || 0) + 1;
      rec.at = now;
      if (rec.attempts >= 5) {
        if (!rec.warned) {
          rec.warned = true;
          console.warn(`Could not re-root nested phase ${nestedPhase.id} after ${rec.attempts} attempts`);
        }
        return;
      }
    } else {
      lastPhaseFixRef.current = { id: nestedPhase.id, at: now, attempts: 1 };
    }

    let rootAncestor = nestedPhase;
    let guard = 0;
    while (!isRootParent(rootAncestor.parent) && guard < 50) {
      const next = store.byId(rootAncestor.parent);
      if (!next) break;
      rootAncestor = next;
      guard += 1;
    }
    if (rootAncestor.id === nestedPhase.id) return;

    // Where was it dropped inside its parent's children? Upper half snaps
    // above the subtree's root phase, lower half snaps below it.
    let droppedInUpperHalf = false;
    if (typeof store.getBranch === "function") {
      const branch = store.getBranch(nestedPhase.id) || [];
      const idx = branch.findIndex(t => t && t.id === nestedPhase.id);
      droppedInUpperHalf = idx >= 0 && branch.length > 0 && idx < branch.length / 2;
    }

    // Correct via a React-state rebuild + gantt remount (same mechanism as
    // handleRearrangePhases). Issuing a corrective move-task exec from here
    // is unreliable and can cascade into an unresponsive render loop.
    if (!rebuildWithPhaseAt(nestedPhase.id, rootAncestor.id, droppedInUpperHalf)) return;

    const phaseName = String(nestedPhase.text || "Phase").trim();
    const targetName = String(rootAncestor.text || "phase").trim();
    toast.info(
      `Phases cannot be nested — "${phaseName}" was placed ${droppedInUpperHalf ? "above" : "below"} "${targetName}"`,
      { autoClose: 2500 }
    );
  }, [rebuildWithPhaseAt]);

  const updateSearchStats = useCallback(() => {
    const query = searchTerm.trim().toLowerCase();
    const api = apiRef.current;
    const stateTasks = api?.getState()?._tasks || [];
    const sourceTasks = stateTasks.length ? stateTasks : eemTasks;
    if (!sourceTasks || sourceTasks.length === 0) return;

    const matchByText = (task) => {
      const text = [
        task?.text,
        task?.details,
        task?.barDescription,
        task?.barMeta,
        task?.categoryLabel,
        task?.subCategoryLabel
      ]
        .filter(Boolean)
        .join(" ");
      return text.toLowerCase().includes(query);
    };

    const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const getRowLabelElement = (rowEl) => {
      if (!rowEl) return null;
      return rowEl.querySelector('.wx-text') || null;
    };

    const clearHighlight = (el) => {
      if (!el) return;
      if (el.dataset.originalSample) {
        el.innerHTML = el.dataset.originalSample;
        delete el.dataset.originalSample;
      }
    };

    const applyHighlight = (el) => {
      if (!el) return;
      if (!query) {
        clearHighlight(el);
        return;
      }
      if (!el.dataset.originalSample) {
        el.dataset.originalSample = el.innerHTML;
      }
      const regex = new RegExp(`(${escapeRegex(query)})`, "ig");
      el.innerHTML = el.dataset.originalSample.replace(regex, '<mark class="search-highlight">$1</mark>');
    };

    const rows = document.querySelectorAll('.wx-row[data-id], .wx-grid-row[data-id]');
    const matchedMeasureIds = new Set();
    if (!query) {
      rows.forEach((rowEl) => clearHighlight(getRowLabelElement(rowEl)));
      setSearchStats({ count: 0, label: "measures", isNoMatch: false });
      return;
    }

    const tasksById = new Map(sourceTasks.map((task) => [String(task.id), task]));

    sourceTasks.forEach((task) => {
      if (task?.isPhase) return;
      const id = String(task.id);
      if (!matchByText(task)) return;

      matchedMeasureIds.add(id);
    });

    const matchesCount = matchedMeasureIds.size;
    setSearchStats({ count: matchesCount, label: "measures", isNoMatch: matchesCount === 0 });

    rows.forEach((rowEl) => {
      const id = rowEl.getAttribute('data-id');
      const task = id ? tasksById.get(id) : null;
      if (!task || task.isPhase) {
        clearHighlight(getRowLabelElement(rowEl));
        return;
      }
      const measureMatched = matchedMeasureIds.has(id);
      const labelEl = getRowLabelElement(rowEl);
      if (labelEl) {
        if (measureMatched) {
          applyHighlight(labelEl);
        } else {
          clearHighlight(labelEl);
        }
      }
    });
  }, [eemTasks, searchTerm]);

  const handleAddPhase = () => {
    openEditorOnAddRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const addButtons = document.querySelectorAll('.wx-collapse');
        if (addButtons && addButtons.length > 0) {
          const firstButton = addButtons[0];
          firstButton.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
          firstButton.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
          firstButton.click();
        }
      });
    });
  };

  const handleRefresh = () => {
    try {
      toast.info('Resetting data', { autoClose: 1200 });
      // Keep behavior simple and consistent with backend-backed load sequence.
      window.location.reload();
    } catch (error) {
      console.error("Reset failed:", error);
      toast.error(`Unable to reset: ${error.message}`);
    }
  };

  const handleRearrangePhases = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;

    const stateTasks = api.getState()?._tasks || [];
    const sourceTasks = stateTasks;
    const orderedTasks = validateTasks(sourceTasks);
    const phaseTasks = orderedTasks.filter((task) => task?.isPhase);

    if (phaseTasks.length < 2) {
      toast.info('At least 2 phases are required to reorder', { autoClose: 1800 });
      return;
    }

    const sourceOrder = new Map(orderedTasks.map((task, index) => [String(task.id), index]));
    const sortedPhases = [...phaseTasks].sort((a, b) => {
      const aStart = parseDateLocal(a.start);
      const bStart = parseDateLocal(b.start);
      const aTime = isNaN(aStart.getTime()) ? Number.MAX_SAFE_INTEGER : aStart.getTime();
      const bTime = isNaN(bStart.getTime()) ? Number.MAX_SAFE_INTEGER : bStart.getTime();
      if (aTime !== bTime) return aTime - bTime;
      return (sourceOrder.get(String(a.id)) ?? 0) - (sourceOrder.get(String(b.id)) ?? 0);
    });

    const phaseIdSet = new Set(phaseTasks.map((task) => String(task.id)));
    const measures = orderedTasks.filter((task) => !task?.isPhase);
    const childMeasuresByPhase = new Map();
    const nonPhaseOrphans = [];

    measures.forEach((measure) => {
      const parentId = String(measure.parent ?? "");
      if (phaseIdSet.has(parentId)) {
        const current = childMeasuresByPhase.get(parentId) || [];
        current.push(measure);
        childMeasuresByPhase.set(parentId, current);
      } else {
        nonPhaseOrphans.push(measure);
      }
    });

    const rebuiltTasks = [];
    sortedPhases.forEach((phase, index) => {
      const desiredName = `Phase ${index + 1}`;
      rebuiltTasks.push({
        ...phase,
        text: desiredName,
        parent: 0,
      });
      const phaseChildren = childMeasuresByPhase.get(String(phase.id)) || [];
      rebuiltTasks.push(...phaseChildren);
    });
    rebuiltTasks.push(...nonPhaseOrphans);

    const previousPhaseOrder = phaseTasks.map((task) => String(task.id)).join("|");
    const newPhaseOrder = sortedPhases.map((task) => String(task.id)).join("|");
    const hasNameChange = sortedPhases.some((phase, index) => String(phase.text || "") !== `Phase ${index + 1}`);
    const stateChanged = previousPhaseOrder !== newPhaseOrder || hasNameChange;

    if (stateChanged) {
      setEemTasks(rebuiltTasks);
      setGanttRenderVersion((prev) => prev + 1);
      setTimeout(() => applyPhaseMeasureClasses(), 0);
      setTimeout(() => applyPhaseMeasureClasses(), 120);
      toast.success('Phases rearranged by start year', { autoClose: 2000 });
    } else {
      toast.info('Phases are already in chronological order', { autoClose: 1800 });
    }
  }, [applyPhaseMeasureClasses, setEemTasks]);

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchStats({ count: 0, label: "matches", isNoMatch: false });
    requestAnimationFrame(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });
  };

  // 1. Save - Simple save without name prompt
  const handleSave = async () => {
    const api = apiRef.current;
    if (!api) return;

    try {
      const state = api.getState();
      const allTasks = state._tasks || [];

      const phases = allTasks.filter(task => task.isPhase);
      const measures = allTasks.filter(task => !task.isPhase);

      const queryProjectId = getProjectIdFromQuery();
      const projectId = queryProjectId || 2167759;

      const backendData = transformToBackendFormat(phases, measures, projectId);

      toast.info('Saving Gantt structure', { autoClose: 2000 });

      await saveGanttStructure(projectId, backendData);

      toast.success('Gantt structure saved, refreshing', { autoClose: 2000 });

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Save failed:", error);
      toast.error(`Unable to save: ${error.message}`, { autoClose: 5000 });
    }
  };

  // 2. Save and Recalculate - Save first, then recalculate
  const handleSaveAndRecalculate = async () => {
    const api = apiRef.current;
    if (!api) return;

    try {
      setRecalculating(true);

      const state = api.getState();
      const allTasks = state._tasks || [];

      const phases = allTasks.filter(task => task.isPhase);
      const measures = allTasks.filter(task => !task.isPhase);

      const queryProjectId = getProjectIdFromQuery();
      const projectId = queryProjectId || 2167759;

      const backendData = transformToBackendFormat(phases, measures, projectId);

      // Step 1: Save
      toast.info('Saving Gantt structure', { autoClose: 2000 });
      await saveGanttStructure(projectId, backendData);
      toast.success('Gantt structure saved', { autoClose: 1500 });

      // Step 2: Recalculate
      toast.info('Recalculating phased EEM', { autoClose: 2000 });
      await reCalculatePhasedEem(projectId);
      toast.success('Phased EEM recalculated and refreshing', { autoClose: 2000 });

      // Reload page to show updated data
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Save and Recalculate failed:", error);
      toast.error(`Operation failed: ${error.message}`, { autoClose: 5000 });
    } finally {
      setRecalculating(false);
    }
  };

  const getMeasureCategoryText = useCallback((measure) => {
    const rawValue =
      measure?.categoryLabel ??
      measure?.originalMeasure?.doeCategory ??
      measure?.originalMeasure?.ecmCategory ??
      measure?.originalMeasure?.category ??
      measure?.originalMeasure?.ecmCategoryId ??
      measure?.doeCategory ??
      measure?.ecmCategory ??
      measure?.category ??
      measure?.ecmCategoryId ??
      "";
    return String(rawValue).replace(/^Category:\s*/i, "").trim();
  }, []);

  const getMeasureSubCategoryText = useCallback((measure) => {
    const rawValue =
      measure?.subCategoryLabel ??
      measure?.originalMeasure?.doeSubCategory ??
      measure?.originalMeasure?.ecmSubCategory ??
      measure?.originalMeasure?.subCategory ??
      measure?.originalMeasure?.ecmSubCategoryId ??
      measure?.doeSubCategory ??
      measure?.ecmSubCategory ??
      measure?.subCategory ??
      measure?.ecmSubCategoryId ??
      "";
    return String(rawValue).replace(/^Subcategory:\s*/i, "").trim();
  }, []);

  const createPhasesFromUnassignedMeasures = useCallback(async (groupBy) => {
    const api = apiRef.current;
    if (!api) return;

    try {
      const actionLabel =
        groupBy === "category"
          ? "Grouping measures by category"
          : groupBy === "subcategory"
            ? "Grouping measures by subcategory"
            : "Auto-creating phases";
      toast.info(actionLabel, { autoClose: 2000 });

      const state = api.getState();
      const allTasks = state._tasks || [];

      // Get existing phases (don't disturb these)
      const existingPhases = allTasks.filter(task => task.isPhase);

      // Get phase IDs to check which measures are already under a phase
      const existingPhaseIds = existingPhases.map(phase => phase.id);

      // Get all measures (tasks that are NOT phases)
      const allMeasures = allTasks.filter(task => !task.isPhase);

      // Filter out measures that are already under an existing phase
      const unassignedMeasures = allMeasures.filter(measure => {
        // If parent is 0, it's not under any phase (root level)
        if (measure.parent === 0) return true;

        // If parent is a phase ID, it's already assigned - skip it
        if (existingPhaseIds.includes(measure.parent)) return false;

        // Otherwise include it
        return true;
      });



      if (unassignedMeasures.length === 0) {
        toast.warning('No unassigned measures found for phase creation');
        return;
      }

      const groupedMeasures = {};
      unassignedMeasures.forEach((measure) => {
        const startYear = new Date(measure.start).getFullYear();
        const category = getMeasureCategoryText(measure) || "Uncategorized";
        const subCategory = getMeasureSubCategoryText(measure) || "Uncategorized";

        let groupKey = String(startYear);
        let groupLabel = `Phase ${startYear}`;

        if (groupBy === "category") {
          groupKey = category;
          groupLabel = category;
        } else if (groupBy === "subcategory") {
          groupKey = subCategory;
          groupLabel = subCategory;
        }

        if (!groupedMeasures[groupKey]) {
          groupedMeasures[groupKey] = {
            label: groupLabel,
            measures: [],
          };
        }

        groupedMeasures[groupKey].measures.push(measure);
      });
      const createdPhases = [];
      const groupKeys = Object.keys(groupedMeasures).sort((a, b) => {
        if (groupBy === "year") return Number(a) - Number(b);
        return a.localeCompare(b);
      });
      const startingPhaseNumber = existingPhases.length + 1;

      groupKeys.forEach((groupKey, index) => {
        const groupEntry = groupedMeasures[groupKey];
        const measuresInGroup = groupEntry?.measures || [];
        if (measuresInGroup.length === 0) return;

        const phaseNumber = startingPhaseNumber + index;
        const phaseName =
          groupBy === "subcategory"
            ? `${groupEntry.label}`
            : `Phase ${phaseNumber}`;

        const dates = measuresInGroup.map(m => new Date(m.start));
        const phaseStart = new Date(Math.min(...dates));

        const endDates = measuresInGroup.map(m => new Date(m.end));
        const phaseEnd = new Date(Math.max(...endDates));

        const measureIds = measuresInGroup.map(m => m.id);

        createdPhases.push({
          id: `temp-phase-${phaseNumber}`,
          text: phaseName,
          start: phaseStart,
          end: phaseEnd,
          progress: 0,
          parent: 0,
          type: "task",
          open: true,
          isPhase: true,
          groupKey,
          measuresInPhase: measureIds
        });
      });


      // Transform to backend format and save
      const queryProjectId = getProjectIdFromQuery();
      const projectId = queryProjectId || 2167759;

      // Helper to format date as date-only (no timezone issues)
      const formatDateLocal = (date) => {
        if (!(date instanceof Date)) return date;

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
      };

      // Combine existing phases + new phases for backend
      const allPhasesForBackend = [
        // Keep existing phases with their original data
        ...existingPhases.map((phase, index) => {
          // Find measures that belong to this existing phase
          const phaseMeasures = allMeasures.filter(m => m.parent === phase.id);

          return {
            id: phase.id, // Keep original ID
            phaseName: phase.text,
            startDate: formatDateLocal(phase.start),
            endDate: formatDateLocal(phase.end),
            parentProject: projectId,
            phaseOrder: index + 1,
            measureIds: phaseMeasures.map(m => m.id),
            active: true
          };
        }),
        // Add new phases
        ...createdPhases.map((phase, index) => ({
          phaseName: phase.text,
          startDate: formatDateLocal(phase.start),
          endDate: formatDateLocal(phase.end),
          parentProject: projectId,
          phaseOrder: existingPhases.length + index + 1,
          measureIds: phase.measuresInPhase,
          active: true
        }))
      ];

      // All measures with their phase assignments
      // Track order counters for each phase to assign eemMeasureOrder
      const phaseOrderCounters = {};

      const allMeasuresForBackend = allMeasures.map(measure => {
        // Check if this measure is already under an existing phase
        const existingPhaseIndex = existingPhases.findIndex(p => p.id === measure.parent);

        let assignedPhase;

        if (existingPhaseIndex !== -1) {
          // Keep existing phase assignment
          assignedPhase = measure.parent;
        } else {
          // Find which new phase this measure belongs to (by checking measuresInPhase array)
          const newPhaseIndex = createdPhases.findIndex(p =>
            p.measuresInPhase.includes(measure.id)
          );

          if (newPhaseIndex !== -1) {
            // Assign to new phase - use phase number (not index)
            assignedPhase = startingPhaseNumber + newPhaseIndex;
          } else {
            // Fallback (shouldn't reach here)
            assignedPhase = 0;
          }
        }

        // Calculate eemMeasureOrder for this phase
        if (!phaseOrderCounters[assignedPhase]) {
          phaseOrderCounters[assignedPhase] = 0;
        }
        phaseOrderCounters[assignedPhase]++;
        const measureOrder = phaseOrderCounters[assignedPhase];

        return {
          ...(measure.originalMeasure || {}),
          id: measure.id,
          phase: assignedPhase,
          eemMeasureOrder: measureOrder,
        };
      });

      const backendData = {
        phases: allPhasesForBackend,
        measures: allMeasuresForBackend
      };


      // Save to backend
      await saveGanttStructure(projectId, backendData);

      toast.success('Phases created and refreshing', { autoClose: 2000 });

      // Reload page
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Phase creation failed:', error);
      toast.error(`Unable to create phases: ${error.message}`);
    }
  }, [getMeasureCategoryText, getMeasureSubCategoryText]);

  // Handle auto-create phases from measures (group by start year)
  const handleAutoCreatePhases = useCallback(async () => {
    await createPhasesFromUnassignedMeasures("year");
  }, [createPhasesFromUnassignedMeasures]);

  // Handle auto-create phases grouped by category
  const handleGroupByCategory = useCallback(async () => {
    await createPhasesFromUnassignedMeasures("category");
  }, [createPhasesFromUnassignedMeasures]);

  // Handle auto-create phases grouped by subcategory
  const handleGroupBySubCategory = useCallback(async () => {
    await createPhasesFromUnassignedMeasures("subcategory");
  }, [createPhasesFromUnassignedMeasures]);

  // Load data on component mount - fetch from backend Gantt and EEM APIs
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Get projectId from query string (with built-in default fallback)
        const projectId = getProjectIdFromQuery();

        // Fetch both Gantt data and EEM measures in parallel
        const [ganttData, measuresData] = await Promise.all([
          fetchGanttData(projectId).catch(err => {
            return null;
          }),
          fetchProjectMeasures(projectId, {
            limit: 100,
            start: 0,
            query: null,
            sort: null
          })
        ]);



        const eemRecords = measuresData?.records || measuresData || [];
        const backendPhases = ganttData?.phases || [];
        const backendMeasures = ganttData?.measures || [];

        // Category debug logs
        console.table(
          (eemRecords || []).map((m) => ({
            id: m?.id,
            ecmCategoryId: m?.ecmCategoryId,
            categoryId: m?.categoryId,
            ecmCategory: m?.ecmCategory,
            category: m?.category,
            subCategory: m?.subCategory
          }))
        );

        let finalTasks = [];

        // Check if we have phases in the Gantt data
        if (backendPhases && backendPhases.length > 0) {

          // Sort phases by phaseOrder
          const sortedPhases = [...backendPhases].sort((a, b) => {
            const orderA = a.phaseOrder || 0;
            const orderB = b.phaseOrder || 0;
            return orderA - orderB;
          });

          // Transform phases to Gantt tasks
          const phasesWithChildren = new Set(backendMeasures.map(m => m.phase));

          const phaseTasks = sortedPhases.map(phase => ({
            id: phase.id,
            text: phase.phaseName,
            start: parseDateLocal(phase.startDate),
            end: parseDateLocal(phase.endDate),
            progress: 0,
            parent: 0, // Phases are root level
            type: "task",
            open: phasesWithChildren.has(phase.id), // only expand if it has child measures
            isPhase: true, // Custom property to identify phases
          }));

          // Transform measures to Gantt tasks and map to phases
          const measureTasks = (backendMeasures || []).map((measure, index) => {
            // Find the original EEM data for this measure
            const originalMeasure = eemRecords.find(eem => eem.id === measure.id) || measure;
            const measureDescription = measure.ecmDescription || originalMeasure.ecmDescription || "Measure";
            const descriptionLine = String(measureDescription).replace(/\s+/g, " ").trim();
            const categoryValue =
              originalMeasure?.doeCategory ??
              measure?.doeCategory ??
              originalMeasure?.ecmCategory ??
              originalMeasure?.category ??
              originalMeasure?.ecmCategoryId ??
              measure?.ecmCategoryId;
            const subCategoryValue =
              originalMeasure?.doeSubCategory ??
              measure?.doeSubCategory ??
              originalMeasure?.ecmSubCategory ??
              originalMeasure?.subCategory ??
              originalMeasure?.ecmSubCategoryId ??
              measure?.ecmSubCategoryId;
            const { categoryLabel, subCategoryLabel, barMeta } = buildDeerMeta(
              categoryValue,
              subCategoryValue
            );
            // Find the parent phase to inherit dates from
            const parentPhase = sortedPhases.find(phase => phase.id === measure.phase);

            let finalStart, finalEnd;

            if (parentPhase) {
              // Use parent phase's start and end dates
              finalStart = parseDateLocal(parentPhase.startDate);
              finalEnd = parseDateLocal(parentPhase.endDate);
            } else {
              // Fallback: Calculate default dates if no parent phase (staggered by 2 days each)
              const today = new Date();
              const defaultStart = new Date(today);
              defaultStart.setDate(today.getDate() + (index * 2));

              const defaultEnd = new Date(defaultStart);
              defaultEnd.setDate(defaultStart.getDate() + 5);

              finalStart = defaultStart;
              finalEnd = defaultEnd;
            }

            return {
              id: measure.id,
              text: descriptionLine,
              start: finalStart,
              end: finalEnd,
              progress: measure.progress || 0,
              parent: measure.phase || 0, // Map to phase ID
              type: "task",
              measureDisplayIndex: index + 1,
              details: descriptionLine,
              barDescription: descriptionLine,
              barMeta,
              categoryLabel,
              subCategoryLabel,
              originalMeasure: originalMeasure, // Store original EEM data
            };
          });

          // Combine phases and measures
          finalTasks = [...phaseTasks, ...measureTasks];
        } else {
          // No phases - show EEM measures as root-level tasks

          finalTasks = transformEemToTasks(eemRecords);
        }

        setEemTasks(finalTasks);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [setEemTasks, setLoading]);

  // Apply custom CSS class to phase bars AND implement custom drag for both phases and measures
  useEffect(() => {
    const liveTasks = apiRef.current?.getState()?._tasks || [];
    if (apiReady && (liveTasks.length > 0 || eemTasks.length > 0)) {
      // Clean up old event listeners FIRST before adding new ones
      const allBars = document.querySelectorAll('.wx-bar');
      allBars.forEach(bar => {
        if (bar._cleanup) {
          bar._cleanup();
          bar._cleanup = null;
        }
      });

      // Wait for DOM to be fully rendered
      setTimeout(() => {
        const stateTasks = apiRef.current?.getState()?._tasks || eemTasks;
        const phaseTasks = stateTasks.filter(task => task.isPhase === true);
        // Get all measures: tasks that are NOT phases (regardless of parent)
        const measureTasks = stateTasks.filter(task => !task.isPhase);
        const api = apiRef.current;

        phaseTasks.forEach(phase => {
          const barElement = document.querySelector(`[data-id="${phase.id}"].wx-bar`);
          const rowElements = document.querySelectorAll(
            `[data-id="${phase.id}"].wx-row, [data-id="${phase.id}"].wx-grid-row`
          );
          rowElements.forEach((rowElement) => {
            rowElement.classList.add('phase-row');
            const nameCell =
              rowElement.querySelector('.wx-cell[data-col-id="text"]') ||
              rowElement.querySelector('.wx-cell[data-column="text"]') ||
              rowElement.querySelector('.wx-cell');
            if (nameCell) {
              nameCell.classList.add('name-cell');
            }
            const collapseButton = rowElement.querySelector('.wx-collapse');
            if (collapseButton) {
              collapseButton.classList.add('collapse-btn');
            }
          });

          if (barElement) {
            barElement.classList.add('phase-bar');

            const calculatePixelsPerYear = () => {
              const chartElement = document.querySelector('.wx-chart') ||
                                   document.querySelector('.wx-scale');
              if (!chartElement) return 200;

              const chartWidth = chartElement.scrollWidth;
              const totalYears = MAX_YEAR - MIN_YEAR;
              return totalYears > 0 ? chartWidth / totalYears : 200;
            };

            let isDragging = false;
            let startX = 0;
            let initialYear = null;
            let pixelsPerYear = 200;

            const handleMouseDown = (e) => {
              if (e.button !== 0) return;

              isDragging = true;
              startX = e.clientX;

              const state = api.getState();
              const currentTask = state._tasks?.find(t => t.id === phase.id);
              const currentStart = currentTask ? new Date(currentTask.start) : new Date(phase.start);
              initialYear = currentStart.getFullYear();
              pixelsPerYear = calculatePixelsPerYear();

              barElement.style.cursor = 'grabbing';
              barElement.style.transition = 'none';
              barElement.style.opacity = '0.8';
              barElement.style.zIndex = '1000';
              barElement.style.pointerEvents = 'none';

              const chartElement = document.querySelector('.wx-chart');
              if (chartElement) chartElement.style.pointerEvents = 'none';

              e.preventDefault();
              e.stopPropagation();
            };

            const handleMouseMove = (e) => {
              if (!isDragging) return;

              const deltaX = e.clientX - startX;
              barElement.style.transform = `translateX(${deltaX}px)`;

              pixelsPerYear = calculatePixelsPerYear();
              const yearChange = Math.round(deltaX / pixelsPerYear);
              const newYear = Math.min(MAX_YEAR, Math.max(MIN_YEAR, initialYear + yearChange));
              barElement.setAttribute('title', `Year: ${newYear}`);

              e.preventDefault();
            };

            const handleMouseUp = (e) => {
              if (!isDragging) return;

              const deltaX = e.clientX - startX;
              const yearChange = Math.round(deltaX / pixelsPerYear);
              let newYear = initialYear + yearChange;
              if (newYear < MIN_YEAR) newYear = MIN_YEAR;
              if (newYear > MAX_YEAR) newYear = MAX_YEAR;

              barElement.style.transition = 'none';
              barElement.style.transform = '';
              barElement.style.opacity = '1';
              barElement.style.zIndex = '';
              barElement.style.pointerEvents = '';

              const chartElement = document.querySelector('.wx-chart');
              if (chartElement) chartElement.style.pointerEvents = '';

              const currentState = api.getState();
              const visibleTasks = currentState._tasks || [];
              const rawTasks =
                currentState.tasks?._tasks ||
                currentState.tasks?.data ||
                currentState.tasks ||
                visibleTasks;

              const flattenTasks = (input) => {
                if (!input) return [];
                if (Array.isArray(input)) {
                  const out = [];
                  const walk = (task) => {
                    if (!task || typeof task !== "object") return;
                    out.push(task);
                    if (Array.isArray(task.data)) {
                      task.data.forEach(walk);
                    }
                  };
                  input.forEach(walk);
                  return out;
                }
                if (typeof input === "object") {
                  return Object.values(input).filter((t) => t && typeof t === "object");
                }
                return [];
              };

              const mergedTasks = [
                ...flattenTasks(eemTasks),
                ...flattenTasks(visibleTasks),
                ...flattenTasks(rawTasks),
              ];
              const taskLookup = new Map();
              mergedTasks.forEach((task) => {
                if (task && task.id !== undefined && task.id !== null) {
                  taskLookup.set(task.id, task);
                }
              });
              const allTasks = Array.from(taskLookup.values());
              const currentPhase =
                taskLookup.get(phase.id) ||
                visibleTasks.find(t => t.id === phase.id) ||
                phase;

              api.exec("update-task", {
                id: phase.id,
                task: {
                  ...currentPhase,
                  start: new Date(newYear, 0, 1)
                }
              });

              const yearDiff = newYear - initialYear;
              const dragDistance = Math.abs(deltaX);
              // Snap ALL child measures to the phase's target year — including
              // measures previously dragged to other years. Skip plain clicks
              // (no real drag) so selecting a phase bar doesn't reshuffle dates.
              if (yearDiff !== 0 || dragDistance > 4) {
                const belongsToPhase = (task) => {
                  if (!task || task.isPhase) return false;
                  let parentId = task.parent;
                  let guard = 0;
                  while (parentId !== 0 && parentId !== "0" && parentId !== null && parentId !== undefined && guard < 50) {
                    if (parentId === phase.id) return true;
                    const parentTask = taskLookup.get(parentId);
                    if (!parentTask) break;
                    parentId = parentTask.parent;
                    guard += 1;
                  }
                  return false;
                };

                const childMeasures = allTasks.filter((task) => belongsToPhase(task));
                childMeasures.forEach(measure => {
                  const currentMeasure = taskLookup.get(measure.id) || measure;
                  const measureStart = new Date(currentMeasure.start);
                  const measureEnd = new Date(currentMeasure.end);
                  const newMeasureStart = new Date(newYear, 0, 1);
                  const durationMs = measureEnd.getTime() - measureStart.getTime();
                  const newMeasureEnd = Number.isFinite(durationMs) && durationMs > 0
                    ? new Date(newMeasureStart.getTime() + durationMs)
                    : new Date(newYear, 11, 31);

                  api.exec("update-task", {
                    id: measure.id,
                    task: {
                      ...currentMeasure,
                      start: newMeasureStart,
                      end: newMeasureEnd
                    }
                  });
                });
              }

              isDragging = false;
              barElement.style.cursor = 'grab';
              e.preventDefault();
            };

            barElement.addEventListener('mousedown', handleMouseDown);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            barElement._cleanup = () => {
              barElement.removeEventListener('mousedown', handleMouseDown);
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            barElement.style.cursor = 'grab';
          }
        });

        // Add custom drag to measures as well

        measureTasks.forEach(measure => {
          const barElement = document.querySelector(`[data-id="${measure.id}"].wx-bar`);
          const rowElements = document.querySelectorAll(
            `[data-id="${measure.id}"].wx-row, [data-id="${measure.id}"].wx-grid-row`
          );
          rowElements.forEach((rowElement) => {
            rowElement.classList.add('item-row');
            const nameCell =
              rowElement.querySelector('.wx-cell[data-col-id="text"]') ||
              rowElement.querySelector('.wx-cell[data-column="text"]') ||
              rowElement.querySelector('.wx-cell');
            if (nameCell) {
              nameCell.classList.add('name-cell');
            }
          });

          if (barElement) {
            barElement.classList.add('item-bar');
            const chartRowFromBar = barElement.closest('.wx-row, .wx-grid-row');
            if (chartRowFromBar) {
              chartRowFromBar.classList.add('item-row');
            }
            // Calculate accurate pixels per year from chart total width
            const calculatePixelsPerYear = () => {
              const chartElement = document.querySelector('.wx-chart') ||
                                   document.querySelector('.wx-scale');

              if (!chartElement) {
                return 200;
              }

              // Get chart width
              const chartWidth = chartElement.scrollWidth;
              const totalYears = MAX_YEAR - MIN_YEAR;

              // Pixels per year = chart width / total years
              const pixelsPerYear = totalYears > 0 ? chartWidth / totalYears : 200;

              return pixelsPerYear;
            };

            // Custom drag implementation for measures
            let isDragging = false;
            let startX = 0;
            let initialYear = null;
            let pixelsPerYear = 200;

            const handleMouseDown = (e) => {
              if (e.button !== 0) return;

              isDragging = true;
              startX = e.clientX;

              const state = api.getState();
              const currentTask = state._tasks?.find(t => t.id === measure.id);
              const currentStart = currentTask ? new Date(currentTask.start) : new Date(measure.start);
              initialYear = currentStart.getFullYear();

              pixelsPerYear = calculatePixelsPerYear();

              barElement.style.cursor = 'grabbing';
              barElement.style.transition = 'none';
              barElement.style.opacity = '0.8';
              barElement.style.zIndex = '1000';
              barElement.style.pointerEvents = 'none';

              const chartElement = document.querySelector('.wx-chart');
              if (chartElement) {
                chartElement.style.pointerEvents = 'none';
              }

              e.preventDefault();
              e.stopPropagation();
            };

            const handleMouseMove = (e) => {
              if (!isDragging) return;

              const deltaX = e.clientX - startX;
              barElement.style.transform = `translateX(${deltaX}px)`;

              // Recalculate pixels per year in real-time to handle dynamic scale changes
              pixelsPerYear = calculatePixelsPerYear();

              const yearChange = Math.round(deltaX / pixelsPerYear);
              const newYear = Math.min(MAX_YEAR, Math.max(MIN_YEAR, initialYear + yearChange));
              barElement.setAttribute('title', `Year: ${newYear}`);

              e.preventDefault();
            };

            const handleMouseUp = (e) => {
              if (!isDragging) return;

              const deltaX = e.clientX - startX;
              const yearChange = Math.round(deltaX / pixelsPerYear);
              let newYear = initialYear + yearChange;

              if (newYear < MIN_YEAR) {
                newYear = MIN_YEAR;
              }
              if (newYear > MAX_YEAR) {
                newYear = MAX_YEAR;
              }

              barElement.style.transition = 'none';
              barElement.style.transform = '';
              barElement.style.opacity = '1';
              barElement.style.zIndex = '';
              barElement.style.pointerEvents = '';

              const chartElement = document.querySelector('.wx-chart');
              if (chartElement) {
                chartElement.style.pointerEvents = '';
              }

              const newStartDate = new Date(newYear, 0, 1);

              const freshState = api.getState();
              const freshMeasure = (freshState._tasks || []).find(t => t.id === measure.id) || measure;

              api.exec("update-task", {
                id: measure.id,
                task: {
                  ...freshMeasure,
                  start: newStartDate
                }
              });

              isDragging = false;
              barElement.style.cursor = 'grab';

              e.preventDefault();
            };

            barElement.addEventListener('mousedown', handleMouseDown);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            barElement._cleanup = () => {
              barElement.removeEventListener('mousedown', handleMouseDown);
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            barElement.style.cursor = 'grab';
          }
        });
      }, 500);
    }

    // Cleanup on unmount
    return () => {
      const allBars = document.querySelectorAll('.wx-bar');
      allBars.forEach(bar => {
        if (bar._cleanup) {
          bar._cleanup();
        }
      });
    };
  }, [
    apiReady,
    eemTasks,
    searchTerm,
    selectedCategory,
    selectedSubCategory,
    showFilters,
    applyPhaseMeasureClasses,
  ]);

  useEffect(() => {
    if (!apiReady) return;
    const applyNow = () => {
      applyPhaseMeasureClasses();
    };

    setTimeout(applyNow, 0);
    setTimeout(applyNow, 80);
  }, [
    apiReady,
    eemTasks,
    searchTerm,
    selectedCategory,
    selectedSubCategory,
    showFilters,
    applyPhaseMeasureClasses,
  ]);

  // Keep bar colors after expand/collapse re-renders.
  useEffect(() => {
    if (!apiReady) return;

    const handleToggle = (e) => {
      const toggleEl = e.target?.closest?.(
        '.wx-toggle-icon, .wx-collapse, [data-action="open-task"], [data-action="close-task"]'
      );
      if (!toggleEl) return;

      // Run multiple passes to cover immediate and delayed DOM updates from the library.
      const rerenderPass = () => {
        applyPhaseMeasureClasses();
      };
      setTimeout(rerenderPass, 0);
      setTimeout(rerenderPass, 80);
      setTimeout(rerenderPass, 220);
      setTimeout(rerenderPass, 500);
    };

    document.addEventListener('click', handleToggle, true);
    return () => {
      document.removeEventListener('click', handleToggle, true);
    };
  }, [apiReady, applyPhaseMeasureClasses]);

  // Virtual scrolling inside the chart re-creates row/bar elements without our
  // custom classes, so watch the gantt DOM and re-tag whenever nodes change.
  // applyPhaseMeasureClasses is idempotent (class adds/label writes are guarded),
  // so the observer settles after one extra pass instead of looping.
  useEffect(() => {
    if (!apiReady) return;

    let observer = null;
    let retryTimer = null;
    let cancelled = false;
    let scheduled = false;
    let trailingTimer = null;
    const runPasses = () => {
      applyPhaseMeasureClasses({ skipLabels: true });
      // Row drag-reorder never emits move-task on the public api, so the
      // no-nested-phases rule is enforced here, once the drag clone is gone.
      // The corrective work must run from a macrotask, not inside rAF.
      if (!document.querySelector('.wx-reorder-task')) {
        setTimeout(() => enforcePhaseRootLevel(), 0);
      }
    };
    // apiReady flips before the gantt DOM exists on a fresh load, so retry
    // until the root element is actually there.
    const attach = () => {
      if (cancelled) return;
      const root = document.querySelector('.svar-gantt-container .wx-gantt');
      if (!root) {
        retryTimer = setTimeout(attach, 300);
        return;
      }
      observer = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          runPasses();
          // The library can patch row/bar attributes (indent padding, labels)
          // after this pass without any childList mutation the observer would
          // see — run one trailing pass to win that race.
          if (trailingTimer) clearTimeout(trailingTimer);
          trailingTimer = setTimeout(runPasses, 250);
        });
      });
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-id'],
      });
      runPasses();
    };
    attach();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (observer) observer.disconnect();
      if (trailingTimer) clearTimeout(trailingTimer);
    };
  }, [apiReady, applyPhaseMeasureClasses, enforcePhaseRootLevel, ganttRenderVersion]);

  // Phases are dragged with a bespoke handler: the library's built-in row
  // reorder would let a phase nest inside another phase (and its internal
  // processing can hang the renderer on that structure). Phase rows therefore
  // never enter the native reorder — they get a root-level snap move instead:
  // drop above or below whichever phase block the pointer ends on.
  useEffect(() => {
    if (!apiReady) return;

    let drag = null; // { id, startY, moved, indicator }
    const ROOT = (p) => p === 0 || p === "0" || p === null || p === undefined;

    const liveTask = (id) => {
      const api = apiRef.current;
      const store = api?.getState?.()?.tasks;
      const byId = typeof store?.byId === "function" ? (v) => store.byId(v) : () => null;
      // Row data-id attributes are strings while pool ids can be numbers.
      const asNum = Number(id);
      return (
        byId(id) ||
        (Number.isNaN(asNum) ? null : byId(asNum)) ||
        api?.getTask?.(id) ||
        (Number.isNaN(asNum) ? null : api?.getTask?.(asNum)) ||
        null
      );
    };

    const rootAncestorOf = (task) => {
      let cursor = task;
      let steps = 0;
      while (cursor && !ROOT(cursor.parent) && steps < 50) {
        const next = liveTask(cursor.parent);
        if (!next) break;
        cursor = next;
        steps += 1;
      }
      return cursor;
    };

    const removeDragVisuals = () => {
      if (!drag) return;
      if (drag.indicator) drag.indicator.remove();
      if (drag.ghost) drag.ghost.remove();
      if (drag.rowEl) drag.rowEl.classList.remove('phase-drag-source');
      document.body.classList.remove('phase-dragging');
    };

    // Which root phase block is the pointer over, and should the drop land
    // above or below it?
    const resolveDropSlot = (clientY, draggedId) => {
      const rows = [...document.querySelectorAll('.svar-gantt-container .wx-row[data-id]')];
      const blocks = new Map(); // rootId -> {top, bottom, task}
      rows.forEach((rowEl) => {
        const task = liveTask(rowEl.getAttribute('data-id'));
        if (!task) return;
        const root = rootAncestorOf(task);
        if (!root || !root.isPhase || root.id === draggedId) return;
        const rect = rowEl.getBoundingClientRect();
        const cur = blocks.get(root.id) || { top: rect.top, bottom: rect.bottom, task: root };
        cur.top = Math.min(cur.top, rect.top);
        cur.bottom = Math.max(cur.bottom, rect.bottom);
        blocks.set(root.id, cur);
      });
      let best = null;
      blocks.forEach((block) => {
        const mid = (block.top + block.bottom) / 2;
        const dist = Math.abs(clientY - mid);
        if (!best || dist < best.dist) {
          best = { ...block, dist, before: clientY < mid };
        }
      });
      return best;
    };

    const onMouseMove = (e) => {
      if (!drag) return;
      if (!drag.moved && Math.abs(e.clientY - drag.startY) < 5) return;
      if (!drag.moved) {
        drag.moved = true;
        document.body.classList.add('phase-dragging');
        if (drag.rowEl) drag.rowEl.classList.add('phase-drag-source');

        const ghost = document.createElement('div');
        ghost.className = 'phase-drag-ghost';
        const title = document.createElement('div');
        title.className = 'phase-drag-ghost-title';
        title.textContent = drag.text;
        const meta = document.createElement('div');
        meta.className = 'phase-drag-ghost-meta';
        meta.textContent = drag.measureCount === 1 ? '1 measure' : `${drag.measureCount} measures`;
        ghost.appendChild(title);
        ghost.appendChild(meta);
        document.body.appendChild(ghost);
        drag.ghost = ghost;

        const indicator = document.createElement('div');
        indicator.className = 'phase-drop-indicator';
        document.body.appendChild(indicator);
        drag.indicator = indicator;
      }

      drag.ghost.style.transform = `translate3d(${e.clientX + 14}px, ${e.clientY + 12}px, 0)`;

      const slot = resolveDropSlot(e.clientY, drag.id);
      if (slot) {
        const y = slot.before ? slot.top : slot.bottom;
        const gridRect = document
          .querySelector('.svar-gantt-container .wx-row[data-id]')
          ?.parentElement?.getBoundingClientRect();
        drag.indicator.style.left = `${gridRect ? gridRect.left : 24}px`;
        drag.indicator.style.width = `${gridRect ? gridRect.width : 320}px`;
        drag.indicator.style.top = `${y - 2}px`;
        drag.indicator.classList.add('visible');
      } else {
        drag.indicator.classList.remove('visible');
      }
    };

    const onMouseUp = (e) => {
      document.removeEventListener('mousemove', onMouseMove, true);
      document.removeEventListener('mouseup', onMouseUp, true);
      if (!drag) return;
      const current = drag;
      removeDragVisuals();
      drag = null;
      const api = apiRef.current;
      if (!api) return;
      if (!current.moved) {
        // Plain click — restore the selection the blocked mousedown would
        // have made.
        try { api.exec('select-task', { id: current.id }); } catch (err) { /* noop */ }
        return;
      }
      const slot = resolveDropSlot(e.clientY, current.id);
      if (!slot || slot.task.id === current.id) return;
      try {
        rebuildWithPhaseAt(current.id, slot.task.id, slot.before);
      } catch (err) {
        console.warn('Phase snap move failed', err);
      }
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      const rowEl = e.target?.closest?.('.svar-gantt-container .wx-row[data-id]');
      if (!rowEl) return;
      // Leave the expand/collapse toggle to the library.
      if (e.target?.closest?.('.wx-toggle-icon, .wx-collapse, .collapse-btn, i[class*="wxi"]')) return;
      const task = liveTask(rowEl.getAttribute('data-id'));
      if (!task || !task.isPhase) return;
      // Keep the library's reorder machinery away from phase rows.
      e.stopPropagation();
      e.preventDefault();
      const store = apiRef.current?.getState?.()?.tasks;
      const pool = store?._pool;
      const poolTasks = pool
        ? (pool instanceof Map ? [...pool.values()] : Object.values(pool))
        : [];
      const measureCount = poolTasks.filter(t => t && !t.isPhase && t.parent === task.id).length;
      drag = {
        id: task.id,
        text: String(task.text || 'Phase').trim(),
        measureCount,
        startY: e.clientY,
        moved: false,
        indicator: null,
        ghost: null,
        rowEl,
      };
      document.addEventListener('mousemove', onMouseMove, true);
      document.addEventListener('mouseup', onMouseUp, true);
    };

    document.addEventListener('mousedown', onMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('mousemove', onMouseMove, true);
      document.removeEventListener('mouseup', onMouseUp, true);
      removeDragVisuals();
      drag = null;
    };
  }, [apiReady, ganttRenderVersion, rebuildWithPhaseAt]);

  useEffect(() => {
    updateSearchStats();
  }, [searchTerm, updateSearchStats]);

  // Listen to add-task events and set default text
  useEffect(() => {
    if (apiRef.current) {
      const api = apiRef.current;

      // Intercept add-task to set default text
      api.intercept("add-task", (event) => {
        // Normalize default text for new phase creation UI.
        if (event.task) {
          const currentText = String(event.task.text || "").trim().toLowerCase();
          if (!currentText || currentText === "new task") {
            event.task.text = "Phase";
          }
        }

        // Force parent to 0 (root level)
        if (event.task) {
          event.task.parent = 0;
          event.task.isPhase = true;
          event.task.type = event.task.type || "task";
          if (!event.task.start) {
            event.task.start = new Date(MIN_YEAR, 0, 1);
          }
          if (!event.task.end) {
            event.task.end = new Date(MIN_YEAR, 11, 31);
          }
        }

        return event;
      });

      // Intercept update-task to snap to January 1st immediately
      api.intercept("update-task", (event) => {
        // Block built-in drag updates for phases AND measures (prevents snap-back / mid-year positions).
        // Custom drag still works because it calls update-task without diff/inProgress.
        if (event?.inProgress === true || event?.diff !== undefined) {
          return false;
        }

        if (event.task && event.task.start) {
          const currentStart = parseDateLocal(event.task.start);
          let year = currentStart.getFullYear();

          // Enforce minimum year
          if (year < MIN_YEAR) {
            year = MIN_YEAR;
          }
          if (year > MAX_YEAR) {
            year = MAX_YEAR;
          }

          // Always set to January 1st of the year
          event.task.start = new Date(year, 0, 1);

        }

        return event;
      });

      // Helper function to log current tamsks as JSON
      const logTasksJSON = () => {
        setTimeout(() => {
          const state = api.getState();
          let allTasks = state.tasks?._tasks ||
                        state.tasks?.data ||
                        state.tasks ||
                        [];

          // If it's not an array, try to convert it
          if (!Array.isArray(allTasks)) {
            allTasks = Object.values(allTasks).filter(t => t && typeof t === 'object');
          }

        }, 100); // Small delay to ensure state is updated
      };

      const addTaskHandler = (event) => {
        // Force grid refresh
        setTimeout(() => {
          if (event.id && event.task) {
            api.exec("update-task", {
              id: event.id,
              task: {
                ...event.task,
                text:
                  (String(event.task.text || "").trim().toLowerCase() === "new task" || !event.task.text)
                    ? "Phase"
                    : event.task.text
              }
            });

            // Log tasks JSON after adding
            logTasksJSON();

            if (openEditorOnAddRef.current && event.task.isPhase) {
              openEditorOnAddRef.current = false;
              pendingNewPhaseIdRef.current = event.id;
              api.exec('show-editor', { id: event.id });
            }

            // Sync React state so the drag useEffect re-runs and attaches drag to the new phase bar
            setTimeout(() => {
              const liveApi = apiRef.current;
              if (!liveApi) return;
              const freshTasks = liveApi.getState()._tasks;
              if (freshTasks && freshTasks.length) setEemTasks([...freshTasks]);
            }, 300);

            // Auto-scroll behavior: scroll right then back to left
            setTimeout(() => {
              const possibleContainers = [
                document.querySelector('.wx-chart'),
                document.querySelector('.wx-gantt'),
                document.querySelector('.wx-layout'),
                document.querySelector('.svar-gantt-container'),
                document.querySelector('[class*="chart"]'),
                document.querySelector('[class*="scroll"]'),
              ];

              const scrollableElement = possibleContainers.find(el =>
                el && el.scrollWidth > el.clientWidth
              );

              if (scrollableElement) {
                const originalScrollLeft = scrollableElement.scrollLeft;

                scrollableElement.scrollTo({
                  left: scrollableElement.scrollLeft + 600,
                  behavior: 'smooth'
                });

                setTimeout(() => {
                  scrollableElement.scrollTo({
                    left: originalScrollLeft,
                    behavior: 'smooth'
                  });
                }, 600);
              }
            }, 200);
          }
        }, 150);
      };

      // Listen to move-task event (when tasks are rearranged)
      const moveTaskHandler = (event) => {
        const resolveTargetPhaseId = (movedTask, allTasks) => {
          const phaseIds = new Set(allTasks.filter(t => t?.isPhase).map(t => t.id));

          // Prefer explicit parent/target fields when present on the move event.
          const eventParentCandidates = [
            event?.parent,
            event?.parentId,
            event?.target,
            event?.targetId,
            event?.to,
            event?.toId,
            event?.newParent,
            event?.task?.parent,
            event?.data?.parent,
          ];
          const explicitPhaseParent = eventParentCandidates.find((id) => phaseIds.has(id));
          if (explicitPhaseParent) return explicitPhaseParent;

          // If parent is already a phase, keep it.
          if (phaseIds.has(movedTask.parent)) return movedTask.parent;

          // Fallback for empty-phase drops: task can end up at root position right below the phase.
          const movedIndex = allTasks.findIndex(t => t.id === movedTask.id);
          if (movedIndex <= 0) return null;

          for (let i = movedIndex - 1; i >= 0; i -= 1) {
            const candidate = allTasks[i];
            if (candidate?.isPhase) return candidate.id;
          }

          return null;
        };

        // If a measure is moved, ensure it is attached to the intended phase, then align dates.
        if (event?.inProgress !== true && event?.id) {
          setTimeout(() => {
            const state = api.getState();
            const allTasks = state?._tasks || [];
            const movedTask = allTasks.find(t => t.id === event.id);

            if (!movedTask) {
              logTasksJSON();
              return;
            }

            if (movedTask.isPhase) {
              // Phases must stay at root level — snap back out if nested.
              enforcePhaseRootLevel();
              logTasksJSON();
              return;
            }

            const targetPhaseId = resolveTargetPhaseId(movedTask, allTasks);
            const parentPhase = allTasks.find(t => t.id === targetPhaseId && t.isPhase);
            if (!parentPhase || !parentPhase.start || !movedTask.start || !movedTask.end) {
              if (!parentPhase) {
                console.warn(`Unable to resolve a valid phase parent for moved measure ${movedTask.id}`, {
                  event,
                  movedTask,
                  targetPhaseId
                });
              }
              logTasksJSON();
              return;
            }

            const phaseYear = new Date(parentPhase.start).getFullYear();
            const measureStart = new Date(movedTask.start);
            const measureEnd = new Date(movedTask.end);
            const yearDiff = phaseYear - measureStart.getFullYear();
            const parentNeedsUpdate = movedTask.parent !== targetPhaseId;

            // Already aligned and already under the right phase.
            if (!parentNeedsUpdate && yearDiff === 0 && measureEnd.getFullYear() === phaseYear) {
              logTasksJSON();
              return;
            }

            // Re-parent by explicit target phase (works reliably even when target phase is collapsed).
            if (parentNeedsUpdate) {
              api.exec("move-task", {
                id: movedTask.id,
                target: targetPhaseId,
                mode: "child"
              });
            }

            // Re-read task after possible indent and then align dates.
            setTimeout(() => {
              const postState = api.getState();
              const postTasks = postState?._tasks || [];
              const postMovedTask = postTasks.find(t => t.id === movedTask.id);
              const postParentPhase = postTasks.find(t => t.id === targetPhaseId && t.isPhase);

              if (!postMovedTask || !postParentPhase || !postMovedTask.start || !postMovedTask.end || !postParentPhase.start) {
                logTasksJSON();
                return;
              }

              const postPhaseYear = new Date(postParentPhase.start).getFullYear();
              const postMeasureStart = new Date(postMovedTask.start);
              const postMeasureEnd = new Date(postMovedTask.end);
              const postYearDiff = postPhaseYear - postMeasureStart.getFullYear();

              if (postYearDiff === 0 && postMeasureEnd.getFullYear() === postPhaseYear) {
                logTasksJSON();
                return;
              }

              const newStart = new Date(postMeasureStart);
              const newEnd = new Date(postMeasureEnd);
              newStart.setFullYear(postMeasureStart.getFullYear() + postYearDiff);
              newEnd.setFullYear(postMeasureEnd.getFullYear() + postYearDiff);

              api.exec("update-task", {
                id: postMovedTask.id,
                task: {
                  ...postMovedTask,
                  start: newStart,
                  end: newEnd
                }
              });

              logTasksJSON();
            }, 30);

            if (parentNeedsUpdate) {
            } else {
            }
          }, 0);
          return;
        }

        setTimeout(() => applyPhaseMeasureClasses(), 0);
        logTasksJSON();
      };

      // Listen to update-task event (when tasks are modified)
      const updateTaskHandler = (event) => {
        logTasksJSON();
      };

      // Listen to delete-task event
      const deleteTaskHandler = (event) => {
        if (editorSaveStateRef.current.suppressToast) {
          editorSaveStateRef.current.suppressToast = false;
          logTasksJSON();
          return;
        }
        // Show notification for phase deletion
        toast.success('Phase deleted successfully', { autoClose: 2000 });
        logTasksJSON();
      };

      // Listen to indent-task event (when tasks become children)
      const indentTaskHandler = (event) => {
        logTasksJSON();
      };

      // Register all event listeners
      api.on("add-task", addTaskHandler);
      api.on("move-task", moveTaskHandler);
      api.on("update-task", updateTaskHandler);
      api.on("delete-task", deleteTaskHandler);
      api.on("indent-task", indentTaskHandler);

      return () => {
        // Cleanup event listeners
        // api.detach("add-task", addTaskHandler);
        // api.detach("move-task", moveTaskHandler);
        // api.detach("update-task", updateTaskHandler);
        // api.detach("delete-task", deleteTaskHandler);
        // api.detach("indent-task", indentTaskHandler);
      };
    }
  }, [apiReady, apiInitVersion]);

  // Track if editor panel is open and handle notifications
  useEffect(() => {
    let editorWasOpen = false;
    let editorSaved = false;

    // Watch for editor panel open/close to show notifications
    const checkEditorState = () => {
      const editorPanel = document.querySelector('.wx-sidebar, .wx-sidearea');
      const isCurrentlyOpen = editorPanel && editorPanel.getBoundingClientRect().width > 0;

      const hideEditorFields = () => {
        if (!editorPanel) return;

        const labelSelectors = [
          '.wx-label',
          '.wx-form-label',
          '.wx-field-label',
          '.wx-field-title',
          'label'
        ];

        const labels = editorPanel.querySelectorAll(labelSelectors.join(','));
        labels.forEach((label) => {
          const text = (label.textContent || '').trim().toLowerCase();
          if (
            text === 'duration' ||
            text === 'type' ||
            text === 'description' ||
            text.startsWith('progress')
          ) {
            const field =
              label.closest('.wx-field') ||
              label.closest('.wx-form-row') ||
              label.closest('.wx-control') ||
              label.closest('.wx-row') ||
              label.parentElement;
            if (field) {
              field.style.display = 'none';
            }
          }

          if (text === 'name') {
            label.textContent = 'Phase Name';
          }
          if (text === 'text') {
            label.textContent = 'Phase';
          }
        });
      };

      if (isCurrentlyOpen && !editorWasOpen) {
        // Editor just opened - check if this is a new phase (task count increased)
        const api = apiRef.current;
        if (api) {
          const state = api.getState();
          const taskCount = state._tasks?.length || 0;
          // Store current count to compare later
          editorPanel._initialTaskCount = taskCount;
        }
        // If a measure is selected, immediately close the editor (phases only)
        if (api) {
          const state = api.getState();
          const selected = state.selected;
          const selectedId = Array.isArray(selected) ? selected[0] : selected;
          const selectedTask = state._tasks?.find(t => t.id === selectedId);
          if (selectedTask && !selectedTask.isPhase) {
            editorSaveStateRef.current.suppressToast = true;
            try {
              api.exec('hide-editor');
            } catch (err) {
              const closeBtn = editorPanel.querySelector('.wxi-close');
              if (closeBtn) {
                closeBtn.click();
              }
            }
            editorWasOpen = false;
            return;
          }
        }
        editorSaved = false;
        editorSaveStateRef.current.suppressToast = false;
        // Mark as saved only when user clicks the editor save button
        const saveBtn = editorPanel.querySelector('button.wx-primary, button[class*="primary"], .wx-button.wx-primary');
        if (saveBtn && !saveBtn._saveListenerAttached) {
          saveBtn._saveListenerAttached = true;
          saveBtn.addEventListener('click', () => {
            if (editorSaveStateRef.current.suppressToast) {
              editorSaveStateRef.current.suppressToast = false;
              return;
            }
            pendingNewPhaseIdRef.current = null;
            editorSaved = true;
          });
        }
        hideEditorFields();
      } else if (!isCurrentlyOpen && editorWasOpen) {
        const pendingNewPhaseId = pendingNewPhaseIdRef.current;
        if (pendingNewPhaseId && !editorSaved) {
          const api = apiRef.current;
          if (api) {
            const state = api.getState();
            const pendingTask = state?._tasks?.find(t => t.id === pendingNewPhaseId);
            if (pendingTask) {
              editorSaveStateRef.current.suppressToast = true;
              api.exec('delete-task', { id: pendingNewPhaseId });
            }
          }
        }
        pendingNewPhaseIdRef.current = null;
        // Editor just closed - show notification
        if (editorSaved) {
          toast.success('Phase saved successfully', { autoClose: 2000 });
          editorSaved = false;
        }
        editorSaveStateRef.current.suppressToast = false;
      } else if (isCurrentlyOpen) {
        hideEditorFields();
        const saveBtn = editorPanel.querySelector('button.wx-primary, button[class*="primary"], .wx-button.wx-primary');
        if (saveBtn && !saveBtn._saveListenerAttached) {
          saveBtn._saveListenerAttached = true;
          saveBtn.addEventListener('click', () => {
            if (editorSaveStateRef.current.suppressToast) {
              editorSaveStateRef.current.suppressToast = false;
              return;
            }
            pendingNewPhaseIdRef.current = null;
            editorSaved = true;
          });
        }
      }

      editorWasOpen = isCurrentlyOpen;
    };

    // Use MutationObserver to detect DOM changes
    const observer = new MutationObserver(() => {
      checkEditorState();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    // Also check periodically as backup
    const interval = setInterval(checkEditorState, 500);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [apiReady]);

  // Handle click outside to close the library's built-in editor panel (right side panel)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Look for the library's editor panel - wx-react-gantt uses .wx-sidebar or .wx-sidearea
      const editorPanel = document.querySelector('.wx-sidebar, .wx-sidearea');

      if (editorPanel) {
        // If this is the add-phase popup flow, cancel it on any outside click/mousedown.
        if (!editorPanel.contains(e.target) && pendingNewPhaseIdRef.current) {
          const pendingNewPhaseId = pendingNewPhaseIdRef.current;
          const api = apiRef.current;
          pendingNewPhaseIdRef.current = null;
          if (api && pendingNewPhaseId) {
            editorSaveStateRef.current.suppressToast = true;
            try {
              api.exec('delete-task', { id: pendingNewPhaseId });
            } catch (err) {
              // ignore if delete-task is unavailable
            }
            try {
              api.exec('hide-editor');
            } catch (err) {
              const closeBtn = editorPanel.querySelector('.wxi-close');
              if (closeBtn) closeBtn.click();
            }
          }
          return;
        }

        // Check if the panel is visible (has content/height)
        const rect = editorPanel.getBoundingClientRect();
        const isVisible = rect.width > 0 && rect.height > 0;
        if (isVisible) {
          // Check if click was outside the editor panel
          if (!editorPanel.contains(e.target)) {
            // Make sure we're not clicking on elements that should open/interact with the editor
            const isInteractiveClick = e.target.closest('.wx-collapse, .wx-toolbar, .wx-grid-row, .wx-bar, button, [data-action]');

            if (!isInteractiveClick) {
              // Click was outside the editor panel - find and click the save button to close
              const saveBtn = editorPanel.querySelector('button.wx-primary, button[class*="primary"], .wx-button.wx-primary');

              if (saveBtn) {
                editorSaveStateRef.current.suppressToast = true;
                saveBtn.click();
              } else {
                // Alternative: try to find any close mechanism
                const api = apiRef.current;
                if (api) {
                  try {
                    api.exec('hide-editor');
                  } catch (err) {
                    // hide-editor API not available - ignore
                  }
                }
              }
            }
          }
        }
      }
    };

    // Use mousedown to make outside-close more reliable in this library's event model.
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [apiReady]);

  // Close editor on Escape key
  useEffect(() => {
    const handleEscapeClose = (e) => {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;

      const editorPanel = document.querySelector('.wx-sidebar, .wx-sidearea');
      if (!editorPanel) return;

      const rect = editorPanel.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0;
      if (!isVisible) return;

      // Prefer the built-in close button behavior.
      const closeBtn = editorPanel.querySelector('.wxi-close');
      if (closeBtn) {
        closeBtn.click();
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Fallback to API close if close button is unavailable.
      const api = apiRef.current;
      if (api) {
        try {
          api.exec('hide-editor');
          e.preventDefault();
          e.stopPropagation();
        } catch (err) {
          // ignore if hide-editor is unavailable
        }
      }
    };

    document.addEventListener('keydown', handleEscapeClose, true);
    return () => {
      document.removeEventListener('keydown', handleEscapeClose, true);
    };
  }, [apiReady]);

  // Prevent editor opening on double-click for measure rows and phase toggle controls.
  useEffect(() => {
    const handleDoubleClick = (e) => {
      const api = apiRef.current;
      if (!api) return;

      const isToggleControl = e.target.closest(
        '.wx-collapse, .wx-toggle-icon, [data-action="open-task"], [data-action="close-task"]'
      );
      if (isToggleControl) {
        // Double-click on expand/collapse should only toggle, never open editor.
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const rowEl = e.target.closest('[data-id].wx-row');
      const barEl = e.target.closest('[data-id].wx-bar');
      const targetEl = rowEl || barEl;

      if (!targetEl) return;

      const idAttr = targetEl.getAttribute('data-id');
      const taskId = idAttr && !Number.isNaN(Number(idAttr)) ? Number(idAttr) : idAttr;
      const state = api.getState();
      const task = state._tasks?.find(t => t.id === taskId);

      if (task && !task.isPhase) {
        e.preventDefault();
        e.stopPropagation();
        editorSaveStateRef.current.suppressToast = true;
        try {
          api.exec('hide-editor');
        } catch (err) {
          // ignore if hide-editor not supported
        }
      }
    };

    document.addEventListener('dblclick', handleDoubleClick, true);
    return () => {
      document.removeEventListener('dblclick', handleDoubleClick, true);
    };
  }, [apiReady]);

  // Prevent scroll jump on task selection clicks (phase/measure).
  useEffect(() => {
    const captureClickStart = (e) => {
      const target = e.target;
      const isGanttInteraction = target?.closest?.('.wx-bar, .wx-row, .wx-grid-row, .wx-task');
      if (!isGanttInteraction) return;

      const scrollTargets = [
        document.scrollingElement,
        document.querySelector('.wx-layout'),
        document.querySelector('.wx-gantt'),
        document.querySelector('.wx-chart'),
        document.querySelector('.svar-gantt-container')
      ].filter(Boolean);

      const scrollSnapshot = scrollTargets.map((el) => ({
        el,
        top: el.scrollTop,
        left: el.scrollLeft
      }));
      clickScrollGuardRef.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        snapshot: scrollSnapshot
      };
    };

    const restoreClickScroll = (e) => {
      const guard = clickScrollGuardRef.current;
      if (!guard.active) return;
      guard.active = false;

      // Do not interfere with drag operations.
      const moved = Math.hypot(e.clientX - guard.startX, e.clientY - guard.startY);
      if (moved > 5) return;

      const scrollSnapshot = guard.snapshot || [];
      const restoreScroll = () => {
        scrollSnapshot.forEach(({ el, top, left }) => {
          el.scrollTop = top;
          el.scrollLeft = left;
        });
        window.scrollTo(window.scrollX, scrollSnapshot[0]?.top ?? window.scrollY);
      };

      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 80);
      setTimeout(restoreScroll, 180);
    };

    document.addEventListener('mousedown', captureClickStart, true);
    document.addEventListener('mouseup', restoreClickScroll, true);
    return () => {
      document.removeEventListener('mousedown', captureClickStart, true);
      document.removeEventListener('mouseup', restoreClickScroll, true);
    };
  }, []);

  // Use EEM tasks if available, otherwise use flat structure demo data
  // Validate and sanitize tasks to prevent library errors
  const validateTasks = (taskList) => {
    return taskList.map(task => ({
      ...task,
      id: task.id || Math.random(),
      text: task.text || "Untitled",
      start: task.start instanceof Date ? task.start : new Date(task.start || new Date()),
      end: task.end instanceof Date ? task.end : new Date(task.end || new Date()),
      progress: task.progress || 0,
      parent: task.parent !== undefined ? task.parent : 0,
      type: task.type || "task",
      ...(task.data ? { data: task.data } : {}), // omit data entirely if empty — library crashes on null.forEach()
    }));
  };

  const prioritizeSearchTasks = useCallback((taskList, rawQuery) => {
    const query = String(rawQuery || "").trim().toLowerCase();
    if (!query || !Array.isArray(taskList) || taskList.length === 0) {
      return taskList;
    }

    const byId = new Map(taskList.map((task) => [String(task.id), task]));
    const phaseTasks = taskList.filter((task) => task?.isPhase);
    const phaseIdSet = new Set(phaseTasks.map((phase) => String(phase.id)));
    const measureTasks = taskList.filter((task) => !task?.isPhase);
    const sourceOrder = new Map(taskList.map((task, index) => [String(task.id), index]));
    const groups = new Map();
    const rootMeasures = [];

    measureTasks.forEach((measure) => {
      const parentId = String(measure.parent ?? 0);
      if (phaseIdSet.has(parentId)) {
        const current = groups.get(parentId) || [];
        current.push(measure);
        groups.set(parentId, current);
        return;
      }
      rootMeasures.push(measure);
    });

    const matchesTask = (task) => {
      const text = [
        task?.text,
        task?.details,
        task?.barDescription,
        task?.barMeta,
        task?.categoryLabel,
        task?.subCategoryLabel
      ]
        .filter(Boolean)
        .join(" ");
      return text.toLowerCase().includes(query);
    };

    const matchedMeasureIds = new Set(
      measureTasks
        .filter((task) => matchesTask(task))
        .map((task) => String(task.id))
    );

    const matchedPhaseIds = new Set();
    phaseTasks.forEach((phase) => {
      const phaseId = String(phase.id);
      const childMeasures = groups.get(phaseId) || [];
      const phaseMatched = matchesTask(phase);
      const hasMatchedMeasure = childMeasures.some((measure) => matchedMeasureIds.has(String(measure.id)));
      if (phaseMatched || hasMatchedMeasure) {
        matchedPhaseIds.add(phaseId);
      }
    });

    const sortByMatchThenSourceOrder = (items, isMatched) => {
      return [...items].sort((a, b) => {
        const aRank = isMatched(a) ? 0 : 1;
        const bRank = isMatched(b) ? 0 : 1;
        if (aRank !== bRank) return aRank - bRank;
        return (sourceOrder.get(String(a.id)) ?? 0) - (sourceOrder.get(String(b.id)) ?? 0);
      });
    };

    const prioritizedPhases = sortByMatchThenSourceOrder(
      phaseTasks,
      (phase) => matchedPhaseIds.has(String(phase.id))
    );

    const result = [];
    prioritizedPhases.forEach((phase) => {
      result.push(phase);
      const phaseId = String(phase.id);
      const phaseMeasures = groups.get(phaseId) || [];
      const prioritizedMeasures = sortByMatchThenSourceOrder(
        phaseMeasures,
        (measure) => matchedMeasureIds.has(String(measure.id))
      );
      result.push(...prioritizedMeasures);
    });

    const prioritizedRootMeasures = sortByMatchThenSourceOrder(
      rootMeasures,
      (measure) => matchedMeasureIds.has(String(measure.id))
    );
    result.push(...prioritizedRootMeasures);

    // Keep any unexpected leftover tasks to avoid accidental data loss in render.
    const addedIds = new Set(result.map((task) => String(task.id)));
    taskList.forEach((task) => {
      const id = String(task.id);
      if (!addedIds.has(id) && byId.has(id)) {
        result.push(task);
      }
    });

    return result;
  }, []);

  const baseTasks = useMemo(() => (eemTasks.length > 0 ? validateTasks(eemTasks) : [
    // Parent tasks with parent: 0
    {
      id: 1,
      text: "Project Planning",
      start: new Date(2024, 3, 2),
      end: new Date(2024, 3, 17),
      progress: 30,
      parent: 0,
      type: "summary",
      open: true,
    },
    {
      id: 2,
      text: "Project Management",
      start: new Date(2024, 3, 2),
      end: new Date(2024, 3, 12),
      progress: 10,
      parent: 0,
      type: "summary",
      open: true,
    },
    {
      id: 3,
      text: "Design Phase",
      start: new Date(2024, 3, 9),
      end: new Date(2024, 3, 25),
      progress: 0,
      parent: 0,
      type: "summary",
      open: true,
    },
    // Child tasks with parent: parentId
    {
      id: 10,
      text: "Marketing Analysis",
      start: new Date(2024, 3, 2),
      end: new Date(2024, 3, 5),
      progress: 100,
      parent: 1,
      type: "task",
    },
    {
      id: 11,
      text: "Discussions",
      start: new Date(2024, 3, 5),
      end: new Date(2024, 3, 7),
      progress: 100,
      parent: 1,
      type: "task",
    },
    {
      id: 12,
      text: "Approval of Strategy",
      start: new Date(2024, 3, 8),
      end: new Date(2024, 3, 8),
      progress: 0,
      parent: 1,
      type: "milestone",
    },
    {
      id: 20,
      text: "Resource Planning",
      start: new Date(2024, 3, 2),
      end: new Date(2024, 3, 6),
      progress: 10,
      parent: 2,
      type: "task",
    },
    {
      id: 21,
      text: "Getting Approval",
      start: new Date(2024, 3, 6),
      end: new Date(2024, 3, 8),
      progress: 10,
      parent: 2,
      type: "task",
    },
    {
      id: 30,
      text: "UI/UX Design",
      start: new Date(2024, 3, 9),
      end: new Date(2024, 3, 16),
      progress: 0,
      parent: 3,
      type: "task",
    },
    {
      id: 31,
      text: "Design Review",
      start: new Date(2024, 3, 16),
      end: new Date(2024, 3, 18),
      progress: 0,
      parent: 3,
      type: "task",
    },
    {
      id: 32,
      text: "Design Approval",
      start: new Date(2024, 3, 18),
      end: new Date(2024, 3, 18),
      progress: 0,
      parent: 3,
      type: "milestone",
    },
  ]), [eemTasks]);

  const getCategoryText = useCallback((task) => {
    const value = task?.categoryLabel || "";
    return String(value).replace(/^Category:\s*/i, "").trim();
  }, []);

  const getSubCategoryText = useCallback((task) => {
    const value = task?.subCategoryLabel || "";
    return String(value).replace(/^Subcategory:\s*/i, "").trim();
  }, []);

  const measureTasks = useMemo(
    () => (Array.isArray(baseTasks) ? baseTasks.filter((task) => !task?.isPhase) : []),
    [baseTasks]
  );

  const categoryOptions = useMemo(() => {
    return Array.from(
      new Set(
        measureTasks
          .map((task) => getCategoryText(task))
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [measureTasks, getCategoryText]);

  const subCategoryOptions = useMemo(() => {
    const source = measureTasks.filter((task) => {
      if (!selectedCategory) return true;
      return getCategoryText(task) === selectedCategory;
    });
    return Array.from(
      new Set(
        source
          .map((task) => getSubCategoryText(task))
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [measureTasks, selectedCategory, getCategoryText, getSubCategoryText]);

  useEffect(() => {
    if (!selectedSubCategory) return;
    if (!subCategoryOptions.includes(selectedSubCategory)) {
      setSelectedSubCategory("");
    }
  }, [selectedSubCategory, subCategoryOptions]);

  const filteredTasks = useMemo(() => {
    const hasCategory = Boolean(selectedCategory);
    const hasSubCategory = Boolean(selectedSubCategory);
    if (!hasCategory && !hasSubCategory) return baseTasks;

    const matchesFilters = (task) => {
      if (task?.isPhase) return false;
      const category = getCategoryText(task);
      const subCategory = getSubCategoryText(task);
      if (hasCategory && category !== selectedCategory) return false;
      if (hasSubCategory && subCategory !== selectedSubCategory) return false;
      return true;
    };

    const phaseIdSet = new Set(
      baseTasks.filter((task) => task?.isPhase).map((task) => String(task.id))
    );
    const matchedMeasureIds = new Set(
      baseTasks
        .filter((task) => !task?.isPhase && matchesFilters(task))
        .map((task) => String(task.id))
    );
    const matchedPhaseIds = new Set(
      baseTasks
        .filter((task) => !task?.isPhase && matchesFilters(task))
        .map((task) => String(task.parent ?? ""))
        .filter((parentId) => phaseIdSet.has(parentId))
    );

    return baseTasks.filter((task) => {
      if (task?.isPhase) return matchedPhaseIds.has(String(task.id));
      return matchedMeasureIds.has(String(task.id));
    });
  }, [
    baseTasks,
    selectedCategory,
    selectedSubCategory,
    getCategoryText,
    getSubCategoryText,
  ]);

  const tasks = useMemo(
    () => prioritizeSearchTasks(filteredTasks, searchTerm),
    [filteredTasks, searchTerm, prioritizeSearchTasks]
  );

  // Disable links (arrow lines between tasks)
  const links = [];

  const scales = [
    { unit: "year", step: 1, format: "yyyy" },
  ];

  const columns = [
    {
      id: "text",
      header: "Phase name",
      width: 310,
      flexgrow: 1,
      template: (value, row) => {
        if (row?.isPhase) return value || "";
        return row?.barDescription || row?.details || value || "";
      },
    },
    {
      id: "start",
      header: "Start date",
      width: 120,
      align: "center",
      template: (value) => {
        if (!value) return "";
        if (value instanceof Date) {
          return value.getFullYear();
        }
        const date = new Date(value);
        if (isNaN(date.getTime())) return "";
        return date.getFullYear();
      },
    },
  ];

  // Fixed date range for the calendar.
  const chartStart = new Date(MIN_YEAR, 0, 1); // January 1, MIN_YEAR (fixed)
  const chartEnd = new Date(MAX_YEAR, 11, 31); // December 31, MAX_YEAR (fixed)

  if (loading) {
    return (
      <div className="svar-gantt-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <p>Loading EEM measures...</p>
      </div>
    );
  }

  return (
    <div className={`svar-gantt-container ${searchTerm.trim() ? "search-reorder-active" : ""}`}>
      <div className="planner-page">
        <div className="planner-card">
          {apiReady && (
            <CustomToolbar
              apiRef={apiRef}
              recalculating={recalculating}
              onRefresh={handleRefresh}
              onAutoCreatePhases={handleAutoCreatePhases}
              onGroupByCategory={handleGroupByCategory}
              onGroupBySubCategory={handleGroupBySubCategory}
              onSave={handleSave}
              onSaveAndRecalculate={handleSaveAndRecalculate}
              onAddPhase={handleAddPhase}
              onRearrangePhases={handleRearrangePhases}
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
              onClearSearch={handleClearSearch}
              searchStats={searchStats}
              searchInputRef={searchInputRef}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters((prev) => !prev)}
              categoryOptions={categoryOptions}
              subCategoryOptions={subCategoryOptions}
              selectedCategory={selectedCategory}
              selectedSubCategory={selectedSubCategory}
              onCategoryChange={handleCategoryChange}
              onSubCategoryChange={handleSubCategoryChange}
            />
          )}
          <Gantt
            key={`main-gantt-${ganttRenderVersion}`}
            tasks={tasks}
            links={links}
            scales={scales}
            columns={columns}
            cellHeight={ROW_BLOCK_HEIGHT}
            api={apiRef}
            readonly={false}
            init={() => {
              setApiReady(true);
              setApiInitVersion(v => v + 1);
            }}
            start={chartStart}
            end={chartEnd}
          />
        </div>
      </div>
    </div>
  );
};

export default SvarBasicGantt;
